// frontend/src/pages/SystemPage.js
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-06-04
// Updated:      2026-10-29 12:50 GMT (EXP-01B.2; theme-aware HMRC panel, ingestion UI)
//
// Description:  System Settings page with tabs:
//               • White Label
//               • Company Info (renamed from Advanced; includes PWA Configuration)
//               • Integrations
//               • Resources
//               • Records Management (mounts AdminMaintenanceWidget inside WidgetWrapper)
//               • Licence Manager (blank)
//
// Updates:
//               • 2026-10-29 GMT: Added HMRC Reference Data pane after Sage and separate Expense Policies tab.
//               • Kept all existing integrations, mobile selector and desktop tabs intact.
//               • Added TomTom Live Traffic Integration pane after Met Office Weather
//               • Uses real backend traffic contract:
//                   - GET  /api/traffic/settings
//                   - PUT  /api/traffic/settings
//                   - GET  /api/traffic/intelligence
//
// PWA Splash Configuration (2026-10-19 10:54 BST):
//               • Added tenant-scoped splash logo and background upload/removal.
//               • Added responsive composed preview using existing PWA background colour.
//               • Uses the versioned Stage 3 asset endpoints; does not modify app icons.
//
// Mobile Responsiveness (2026-09-04):
//               • Added a dedicated <=768px presentation for System Settings.
//               • Mobile uses a full-width section selector instead of compressing the desktop tabs.
//               • Multi-column forms collapse to a single column; controls become touch-friendly.
//               • Desktop layout and all existing API/settings behaviour remain unchanged.
//               • Resources and Records Management continue to mount their existing components.

import React, { useState, useEffect, useMemo } from "react";
import { useTheme } from "../context/ThemeContext.jsx";
import api from "../api/axiosInstance";
import ResourcesTabContainer from "../components/ResourcesTabContainer.jsx";
import RecordsManagementPanel from "../components/RecordsManagementPanel.jsx";
import HmrcReferencePanel from "../components/HmrcReferencePanel.jsx";

// ---------- Helpers ----------
const safeColor = (value) => (/^#[0-9A-Fa-f]{6}$/.test(value) ? value : "#000000");
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MOBILE_BREAKPOINT = 768;

function useIsMobile(breakpoint = MOBILE_BREAKPOINT) {
  const [isMobile, setIsMobile] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia(`(max-width: ${breakpoint}px)`).matches;
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;

    const mediaQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const apply = () => setIsMobile(mediaQuery.matches);

    apply();

    if (mediaQuery.addEventListener) mediaQuery.addEventListener("change", apply);
    else mediaQuery.addListener(apply);

    return () => {
      if (mediaQuery.removeEventListener) mediaQuery.removeEventListener("change", apply);
      else mediaQuery.removeListener(apply);
    };
  }, [breakpoint]);

  return isMobile;
}

const defaultBusinessHours = () =>
  DAYS.map((d) => ({
    day: d,
    closed: d === "Sat" || d === "Sun",
    open: "09:00",
    close: "17:00",
  }));

const emptyOrganisation = (tenantId = null) => ({
  id: undefined,
  tenant_id: tenantId,
  company_name: "",
  company_number: "",
  sic_code: "",
  vat_number: "",
  email: "",
  telephone: "",
  address_line_1: "",
  address_line_2: "",
  address_line_3: "",
  city: "",
  county: "",
  postcode: "",
  trading_since: "",
  business_hours: defaultBusinessHours(),
  public_holidays: [],
  custom_closures: [],
});

const emptyPwaSettings = () => ({
  id: null,
  tenant_id: null,
  enabled: false,
  app_name: "Vision Systems",
  short_name: "Vision Systems",
  display_mode: "standalone",
  theme_color: "#172d41",
  background_color: "#ffffff",
  start_url: "/dashboard",
  scope: "/",
  icon_version: 0,
  icon_updated_at: null,
  has_icons: false,
  has_active_hostname: false,
  ready_to_enable: false,
  icon_urls: {},
  splash_logo_version: 0,
  splash_logo_updated_at: null,
  splash_background_version: 0,
  splash_background_updated_at: null,
  has_splash_logo: false,
  has_splash_background: false,
  splash_logo_url: null,
  splash_background_url: null,
});

const emptySharePoint = (tenantId = null) => ({
  id: undefined,
  tenant_id: tenantId,
  enabled: false,
  domain: "",
  site_directory: "",
  site_id: "",
  clients_drive_id: "",
  root_path: "/",
  aad_tenant_id: "",
  client_id: "",
  access_token_masked: null,
  client_secret_plain: "",
  token_expires_at: null,
  last_sync_at: null,
  last_error: null,
  created_at: null,
  updated_at: null,
});

// Messaging / SendGrid / Twilio (UI + DB-backed fields)
const emptyMessaging = (tenantId = null) => ({
  id: undefined,
  tenant_id: tenantId,
  sendgrid_from_name: "",
  sendgrid_from_email: "",
  reply_to_email: "",
  whatsapp_from_number: "",
  email_dry_run: true,
  enable_email: false,
  enable_whatsapp: false,
  enable_sms: false,
  enable_livechat: false,
  send_usage_report: false,
  usage_report_email: "",
  sms_sender_id: "",
  sms_from_number: "",
  whatsapp_business_name: "",
  whatsapp_template_sid: "",
  twilio_account_sid: "",
  twilio_messaging_service_sid: "",
  sendgrid_api_key_plain: "",
  twilio_api_key_plain: "",
  sendgrid_api_key_masked: null,
  twilio_api_key_masked: null,
  twilio_account_sid_masked: null,
  twilio_messaging_service_sid_masked: null,
  test_email: "",
  test_number: "",
});

// Weather (Met Office) settings (tenant-scoped, secrets masked)
const emptyWeather = () => ({
  tenant_id: null,
  enable_weather: false,
  default_source: "BD1",
  cache_ttl_seconds: 300,
  metoffice_global_spot_api_key_masked: null,
  metoffice_bpf_api_key_masked: null,
  metoffice_global_spot_api_key_plain: "",
  metoffice_bpf_api_key_plain: "",
});

// Traffic (TomTom) settings (tenant-scoped)
const emptyTraffic = () => ({
  id: 0,
  tenant_id: null,
  provider: "tomtom",
  is_enabled: false,
  api_key: "",
  base_url: "https://api.tomtom.com/traffic/services/4/flowSegmentData/absolute/10/json",
  unit: "KMPH",
  zoom: 10,
  cache_ttl_seconds: 300,
  connect_timeout_seconds: 3,
  read_timeout_seconds: 6,
  persist_snapshots: true,
  max_requests_per_job_check: 4,
  notes: "",
  created_at: null,
  updated_at: null,
});

const SystemPage = () => {
  const { theme, refreshTheme } = useTheme();
  const isMobile = useIsMobile();
  const [activeTab, setActiveTab] = useState("whitelabel");
  const [resourcesKey, setResourcesKey] = useState(0);

  // ---------------- White Label State ----------------
  const [form, setForm] = useState({
    client_name: "",
    primary_color: "#000000",
    secondary_color: "#000000",
    accent_color: "#000000",
    primary_hover: "#333333",
    secondary_hover: "#ff7a7a",
    accent_hover: "#ffa366",
    font_header: "Arial",
    font_body: "Arial",
    button_general: { normal: "#000000", hover: "#000000", click: "#000000" },
    button_action: { normal: "#000000", hover: "#000000", click: "#000000" },
    logo_login_url: "",
    logo_header_url: "",
  });

  const [loginPreview, setLoginPreview] = useState(null);
  const [headerPreview, setHeaderPreview] = useState(null);
  const [isHovering, setIsHovering] = useState(false);
  const [isClicking, setIsClicking] = useState(false);

  const getButtonColor = () => {
    if (isClicking) return form.button_general?.click;
    if (isHovering) return form.button_general?.hover;
    return form.button_general?.normal;
  };

  // Load UI config
  useEffect(() => {
    api
      .get("/ui-config")
      .then((res) => {
        const data = res.data || {};
        const payload = {
          ...data,
          primary_color: safeColor(data.primary_color),
          secondary_color: safeColor(data.secondary_color),
          accent_color: safeColor(data.accent_color),
          primary_hover: safeColor(data.primary_hover),
          secondary_hover: safeColor(data.secondary_hover),
          accent_hover: safeColor(data.accent_hover),
          button_general: {
            normal: safeColor(data.button_general?.normal || "#000000"),
            hover: safeColor(data.button_general?.hover || "#000000"),
            click: safeColor(data.button_general?.click || "#000000"),
          },
          button_action: {
            normal: safeColor(data.button_action?.normal || "#000000"),
            hover: safeColor(data.button_action?.hover || "#000000"),
            click: safeColor(data.button_action?.click || "#000000"),
          },
        };
        setForm(payload);
        setLoginPreview(payload.logo_login_url || null);
        setHeaderPreview(payload.logo_header_url || null);
      })
      .catch((err) => console.error("Failed to load UI config:", err));
  }, []);

  const handleChange = (field, value) => setForm((prev) => ({ ...prev, [field]: value }));
  const handleNestedChange = (group, key, value) =>
    setForm((prev) => ({ ...prev, [group]: { ...prev[group], [key]: value } }));

  const handleFileChange = (field, file) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result;
      setForm((prev) => ({ ...prev, [field]: base64 }));
      if (field === "logo_login_url") setLoginPreview(base64);
      if (field === "logo_header_url") setHeaderPreview(base64);
    };
    if (file) reader.readAsDataURL(file);
  };

  const handleSaveBranding = async () => {
    const payload = {
      ...form,
      primary_color: safeColor(form.primary_color),
      secondary_color: safeColor(form.secondary_color),
      accent_color: safeColor(form.accent_color),
      primary_hover: safeColor(form.primary_hover),
      secondary_hover: safeColor(form.secondary_hover),
      accent_hover: safeColor(form.accent_hover),
      button_general: {
        normal: safeColor(form.button_general.normal),
        hover: safeColor(form.button_general.hover),
        click: safeColor(form.button_general.click),
      },
      button_action: {
        normal: safeColor(form.button_action.normal),
        hover: safeColor(form.button_action.hover),
        click: safeColor(form.button_action.click),
      },
    };

    try {
      const res = await api.put("/ui-config", payload);
      if (res.status === 200) {
        const root = document.documentElement;
        root.style.setProperty("--btn-general-normal", payload.button_general.normal);
        root.style.setProperty("--btn-general-hover", payload.button_general.hover);
        root.style.setProperty("--btn-general-click", payload.button_general.click);
        root.style.setProperty("--btn-action-normal", payload.button_action.normal);
        root.style.setProperty("--btn-action-hover", payload.button_action.hover);
        root.style.setProperty("--btn-action-click", payload.button_action.click);
        alert("Configuration saved successfully.");
        refreshTheme();
      }
    } catch (error) {
      console.error("Save failed:", error?.response || error?.message || error);
      alert("Save failed.");
    }
  };

  // ---------------- Company Info (Organisation) State ----------------
  const [tenantId, setTenantId] = useState(null);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgSaving, setOrgSaving] = useState(false);
  const [org, setOrg] = useState(() => emptyOrganisation(null));

  // ---------------- Company / PWA State ----------------
  const [pwaAllowed, setPwaAllowed] = useState(null);
  const [pwaLoading, setPwaLoading] = useState(false);
  const [pwaSaving, setPwaSaving] = useState(false);
  const [pwaUploading, setPwaUploading] = useState(false);
  const [pwaSplashBusy, setPwaSplashBusy] = useState(null);
  const [pwaHostnameSaving, setPwaHostnameSaving] = useState(false);
  const [pwa, setPwa] = useState(() => emptyPwaSettings());
  const [pwaHostnames, setPwaHostnames] = useState([]);
  const [newPwaHostname, setNewPwaHostname] = useState("");
  const [newPwaHostnamePrimary, setNewPwaHostnamePrimary] = useState(false);

  // ---------------- Integrations / SharePoint State ----------------
  const [spLoading, setSpLoading] = useState(false);
  const [spSaving, setSpSaving] = useState(false);
  const [sp, setSp] = useState(() => emptySharePoint(null));
  const [spTesting, setSpTesting] = useState(false);
  const [spTest, setSpTest] = useState(null);

  // Placeholder Sage state
  const [sage, setSage] = useState({
    enabled: false,
    base_url: "",
    company_id: "",
    client_id: "",
    has_secret: false,
    secret_preview: null,
    client_secret_plain: "",
  });

  // Messaging / SendGrid / Twilio state
  const [messaging, setMessaging] = useState(() => emptyMessaging(null));
  const [msgLoading, setMsgLoading] = useState(false);

  // Weather / Met Office state
  const [weather, setWeather] = useState(() => emptyWeather());
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherSaving, setWeatherSaving] = useState(false);
  const [weatherTesting, setWeatherTesting] = useState(false);
  const [weatherTest, setWeatherTest] = useState(null);

  // Traffic / TomTom state
  const [traffic, setTraffic] = useState(() => emptyTraffic());
  const [trafficLoading, setTrafficLoading] = useState(false);
  const [trafficSaving, setTrafficSaving] = useState(false);
  const [trafficTesting, setTrafficTesting] = useState(false);
  const [trafficTest, setTrafficTest] = useState(null);

  // Accordion flags
  const [holidaysOpen, setHolidaysOpen] = useState(false);
  const [closuresOpen, setClosuresOpen] = useState(false);

  // Fetch tenant_id from /users/me
  useEffect(() => {
    api
      .get("/users/me")
      .then((r) => {
        const tid = r?.data?.tenant_id ?? 1;
        setTenantId(tid);
        setMessaging((prev) => ({
          ...emptyMessaging(tid),
          ...prev,
          tenant_id: tid,
        }));
      })
      .catch(() => {
        setTenantId(1);
        setMessaging((prev) => ({
          ...emptyMessaging(1),
          ...prev,
          tenant_id: 1,
        }));
      });
  }, []);

  // Load PWA configuration. Backend RBAC is authoritative; non-admin users receive 403.
  useEffect(() => {
    let cancelled = false;

    const loadPwa = async () => {
      setPwaLoading(true);
      try {
        const [settingsRes, hostnamesRes] = await Promise.all([
          api.get("/pwa/settings"),
          api.get("/pwa/hostnames"),
        ]);

        if (cancelled) return;

        setPwa({
          ...emptyPwaSettings(),
          ...(settingsRes.data || {}),
          icon_urls: settingsRes.data?.icon_urls || {},
        });
        setPwaHostnames(Array.isArray(hostnamesRes.data) ? hostnamesRes.data : []);
        setPwaAllowed(true);
      } catch (e) {
        if (cancelled) return;

        if (e?.response?.status === 403) {
          setPwaAllowed(false);
          setPwaHostnames([]);
        } else {
          console.warn("Failed to load PWA configuration:", e?.response?.data || e?.message || e);
          setPwaAllowed(null);
        }
      } finally {
        if (!cancelled) setPwaLoading(false);
      }
    };

    loadPwa();

    return () => {
      cancelled = true;
    };
  }, []);

  // Refresh Resources tab
  useEffect(() => {
    const bump = () => setResourcesKey((k) => k + 1);
    window.addEventListener("resource:updated", bump);
    return () => window.removeEventListener("resource:updated", bump);
  }, []);

  // Load organisation
  useEffect(() => {
    if (!tenantId) return;
    setOrgLoading(true);
    api
      .get("/organisation", { params: { tenant_id: tenantId } })
      .then((res) => {
        const data = res.data;
        if (data && typeof data === "object") {
          setOrg({
            ...emptyOrganisation(tenantId),
            ...data,
            business_hours:
              Array.isArray(data.business_hours) && data.business_hours.length === 7
                ? data.business_hours
                : defaultBusinessHours(),
            public_holidays: Array.isArray(data.public_holidays) ? data.public_holidays : [],
            custom_closures: Array.isArray(data.custom_closures) ? data.custom_closures : [],
          });
        } else {
          setOrg(emptyOrganisation(tenantId));
        }
      })
      .catch((e) => {
        console.warn("Failed to load organisation:", e?.response?.data || e?.message || e);
        setOrg(emptyOrganisation(tenantId));
      })
      .finally(() => setOrgLoading(false));
  }, [tenantId]);

  // Load SharePoint settings
  useEffect(() => {
    if (!tenantId) return;
    setSpLoading(true);
    api
      .get("/sharepoint/settings", { params: { tenant_id: tenantId } })
      .then((res) => {
        const data = res.data || {};
        setSp({
          ...emptySharePoint(tenantId),
          ...data,
          tenant_id: tenantId,
          client_secret_plain: "",
          access_token_masked: data.access_token_masked || null,
        });
      })
      .catch((e) => {
        if (e?.response?.status === 404) {
          setSp(emptySharePoint(tenantId));
        } else {
          console.warn("Failed to load SharePoint settings:", e?.response?.data || e?.message || e);
          setSp(emptySharePoint(tenantId));
        }
      })
      .finally(() => setSpLoading(false));
  }, [tenantId]);

  // Load messaging settings
  useEffect(() => {
    if (!tenantId) return;
    setMsgLoading(true);
    api
      .get("/messaging/settings", { params: { tenant_id: tenantId } })
      .then((res) => {
        const data = res.data || {};
        setMessaging((prev) => ({
          ...prev,
          id: data.id ?? prev.id,
          tenant_id: data.tenant_id ?? tenantId,
          sendgrid_from_name: data.sendgrid_from_name || "",
          sendgrid_from_email: data.sendgrid_from_email || "",
          reply_to_email: data.reply_to_email || "",
          whatsapp_from_number: data.whatsapp_from_number || "",
          email_dry_run:
            typeof data.email_dry_run === "boolean"
              ? data.email_dry_run
              : prev.email_dry_run,
          enable_email:
            typeof data.enable_email === "boolean"
              ? data.enable_email
              : prev.enable_email,
          enable_whatsapp:
            typeof data.enable_whatsapp === "boolean"
              ? data.enable_whatsapp
              : prev.enable_whatsapp,
          enable_sms:
            typeof data.enable_sms === "boolean"
              ? data.enable_sms
              : prev.enable_sms,
          enable_livechat:
            typeof data.enable_livechat === "boolean"
              ? data.enable_livechat
              : prev.enable_livechat,
          send_usage_report:
            typeof data.send_usage_report === "boolean"
              ? data.send_usage_report
              : prev.send_usage_report,
          usage_report_email: data.usage_report_email || prev.usage_report_email || "",
          sms_sender_id: data.sms_sender_id || prev.sms_sender_id || "",
          sms_from_number: data.sms_from_number || prev.sms_from_number || "",
          whatsapp_business_name:
            data.whatsapp_business_name || prev.whatsapp_business_name || "",
          whatsapp_template_sid:
            data.whatsapp_template_sid || prev.whatsapp_template_sid || "",
          sendgrid_api_key_masked:
            data.sendgrid_api_key_masked ?? prev.sendgrid_api_key_masked ?? null,
          twilio_api_key_masked:
            data.twilio_api_key_masked ?? prev.twilio_api_key_masked ?? null,
          twilio_account_sid_masked:
            data.twilio_account_sid_masked ??
            prev.twilio_account_sid_masked ??
            null,
          twilio_messaging_service_sid_masked:
            data.twilio_messaging_service_sid_masked ??
            prev.twilio_messaging_service_sid_masked ??
            null,
          sendgrid_api_key_plain: "",
          twilio_api_key_plain: "",
          twilio_account_sid: "",
          twilio_messaging_service_sid: "",
        }));
      })
      .catch((e) => {
        console.warn("Failed to load messaging settings:", e?.response?.data || e?.message || e);
        setMessaging((prev) => ({
          ...emptyMessaging(tenantId),
          ...prev,
          tenant_id: tenantId,
        }));
      })
      .finally(() => setMsgLoading(false));
  }, [tenantId]);

  // Load weather settings
  useEffect(() => {
    if (!tenantId) return;
    setWeatherLoading(true);
    api
      .get("/weather/settings")
      .then((res) => {
        const data = res.data || {};
        setWeather((prev) => ({
          ...prev,
          tenant_id: data.tenant_id ?? prev.tenant_id ?? tenantId,
          enable_weather: typeof data.enable_weather === "boolean" ? data.enable_weather : false,
          default_source: data.default_source || "BD1",
          cache_ttl_seconds:
            typeof data.cache_ttl_seconds === "number"
              ? data.cache_ttl_seconds
              : prev.cache_ttl_seconds,
          metoffice_global_spot_api_key_masked:
            data.metoffice_global_spot_api_key_masked ?? null,
          metoffice_bpf_api_key_masked: data.metoffice_bpf_api_key_masked ?? null,
          metoffice_global_spot_api_key_plain: "",
          metoffice_bpf_api_key_plain: "",
        }));
      })
      .catch((e) => {
        console.warn("Failed to load weather settings:", e?.response?.data || e?.message || e);
        setWeather((prev) => ({
          ...emptyWeather(),
          ...prev,
        }));
      })
      .finally(() => setWeatherLoading(false));
  }, [tenantId]);

  // Load traffic settings
  useEffect(() => {
    if (!tenantId) return;
    setTrafficLoading(true);
    api
      .get("/traffic/settings")
      .then((res) => {
        const data = res.data || {};
        setTraffic({
          ...emptyTraffic(),
          ...data,
          tenant_id: data.tenant_id ?? tenantId,
          provider: data.provider || "tomtom",
          unit: data.unit || "KMPH",
          api_key: data.api_key || "",
          base_url:
            data.base_url ||
            "https://api.tomtom.com/traffic/services/4/flowSegmentData/absolute/10/json",
          notes: data.notes || "",
        });
      })
      .catch((e) => {
        console.warn("Failed to load traffic settings:", e?.response?.data || e?.message || e);
        setTraffic({
          ...emptyTraffic(),
          tenant_id: tenantId,
        });
      })
      .finally(() => setTrafficLoading(false));
  }, [tenantId]);

  // ----- Styles -----
  const labelStyle = useMemo(
    () => ({ fontSize: 14, fontWeight: 700, marginTop: 6, color: "#333" }),
    []
  );

  const inputStyle = useMemo(
    () => ({
      padding: "8px",
      borderRadius: "6px",
      border: "1px solid #ccc",
      fontFamily: theme.font_body,
      marginTop: "6px",
      width: "100%",
      boxSizing: "border-box",
      height: isMobile ? "44px" : "34px",
      fontSize: isMobile ? 16 : undefined,
    }),
    [isMobile, theme.font_body]
  );

  const selectStyle = useMemo(
    () => ({
      ...inputStyle,
      lineHeight: "20px",
      appearance: "none",
      WebkitAppearance: "none",
      MozAppearance: "none",
      backgroundImage:
        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='%23333'><path d='M7 10l5 5 5-5z'/></svg>\")",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "right 8px center",
      paddingRight: 34,
    }),
    [inputStyle]
  );

  const sectionStyle = useMemo(
    () => ({
      background: "#fff",
      padding: isMobile ? "12px" : "20px",
      display: "flex",
      flexDirection: "column",
      borderRadius: isMobile ? "10px" : "0 0 10px 10px",
      border: "1px solid #ddd",
      borderTop: "1px solid #ccc",
      gap: isMobile ? "14px" : "20px",
      minWidth: 0,
      overflowX: "hidden",
    }),
    [isMobile]
  );

  const actionBtn = useMemo(
    () => ({
      background: "var(--btn-action-normal, #1f7a8c)",
      color: "#fff",
      padding: isMobile ? "10px 12px" : "6px 10px",
      border: "none",
      borderRadius: 8,
      cursor: "pointer",
      fontWeight: 600,
      minHeight: isMobile ? 44 : undefined,
    }),
    [isMobile]
  );

  const generalBtn = useMemo(
    () => ({
      background: "var(--btn-general-normal, #222)",
      color: "#fff",
      padding: isMobile ? "10px 12px" : "6px 10px",
      border: "none",
      borderRadius: 8,
      cursor: "pointer",
      fontWeight: 600,
      minHeight: isMobile ? 44 : undefined,
    }),
    [isMobile]
  );

  const dangerBtn = useMemo(
    () => ({
      background: "var(--btn-danger-normal, #8c1f1f)",
      color: "#fff",
      padding: isMobile ? "10px 12px" : "6px 10px",
      border: "none",
      borderRadius: 8,
      cursor: "pointer",
      fontWeight: 600,
      minHeight: isMobile ? 44 : undefined,
    }),
    [isMobile]
  );

  // ----- Organisation handlers -----
  const setOrgField = (key, value) => setOrg((prev) => ({ ...prev, [key]: value }));

  const setHour = (index, patch) =>
    setOrg((prev) => {
      const list = [...prev.business_hours];
      list[index] = { ...list[index], ...patch };
      return { ...prev, business_hours: list };
    });

  const addHoliday = (dateStr) => {
    if (!dateStr) return;
    setOrg((prev) => {
      if (prev.public_holidays.includes(dateStr)) return prev;
      return { ...prev, public_holidays: [...prev.public_holidays, dateStr] };
    });
  };

  const removeHoliday = (idx) =>
    setOrg((prev) => {
      const list = [...prev.public_holidays];
      list.splice(idx, 1);
      return { ...prev, public_holidays: list };
    });

  const addClosure = () =>
    setOrg((prev) => ({
      ...prev,
      custom_closures: [...prev.custom_closures, { date: "", period: "FULL", note: "" }],
    }));

  const updateClosure = (idx, patch) =>
    setOrg((prev) => {
      const list = [...prev.custom_closures];
      list[idx] = { ...list[idx], ...patch };
      return { ...prev, custom_closures: list };
    });

  const removeClosure = (idx) =>
    setOrg((prev) => {
      const list = [...prev.custom_closures];
      list.splice(idx, 1);
      return { ...prev, custom_closures: list };
    });

  const saveOrganisation = async () => {
    if (!tenantId) {
      alert("Tenant not resolved yet. Please try again.");
      return;
    }
    setOrgSaving(true);
    const payload = { ...org, tenant_id: tenantId };

    try {
      if (org?.id) {
        const res = await api.put(`/organisation/${org.id}`, payload);
        setOrg({ ...emptyOrganisation(tenantId), ...(res.data || payload) });
        alert("Organisation updated.");
      } else {
        const res = await api.post("/organisation", payload);
        setOrg({ ...emptyOrganisation(tenantId), ...(res.data || payload) });
        alert("Organisation created.");
      }
    } catch (e) {
      console.error("Failed to save organisation:", e?.response?.data || e?.message || e);
      alert("Save failed.");
    } finally {
      setOrgSaving(false);
    }
  };

  // ----- PWA handlers -----
  const refreshPwaConfiguration = async () => {
    try {
      const [settingsRes, hostnamesRes] = await Promise.all([
        api.get("/pwa/settings"),
        api.get("/pwa/hostnames"),
      ]);

      setPwa({
        ...emptyPwaSettings(),
        ...(settingsRes.data || {}),
        icon_urls: settingsRes.data?.icon_urls || {},
      });
      setPwaHostnames(Array.isArray(hostnamesRes.data) ? hostnamesRes.data : []);
      setPwaAllowed(true);
      return true;
    } catch (e) {
      if (e?.response?.status === 403) {
        setPwaAllowed(false);
        setPwaHostnames([]);
        return false;
      }
      throw e;
    }
  };

  const setPwaField = (key, value) =>
    setPwa((prev) => ({
      ...prev,
      [key]: value,
    }));

  const savePwaSettings = async () => {
    const appName = String(pwa.app_name || "").trim();
    const shortName = String(pwa.short_name || "").trim();
    const startUrl = String(pwa.start_url || "").trim();
    const scope = String(pwa.scope || "").trim();

    if (!appName) {
      alert("PWA App Name is required.");
      return;
    }
    if (!shortName) {
      alert("PWA Short Name is required.");
      return;
    }
    if (!startUrl.startsWith("/") || startUrl.startsWith("//")) {
      alert("PWA Start URL must be an application path beginning with /.");
      return;
    }
    if (!scope.startsWith("/") || scope.startsWith("//")) {
      alert("PWA Scope must be an application path beginning with /.");
      return;
    }
    if (pwa.enabled && !pwa.ready_to_enable) {
      alert("The PWA cannot be enabled until at least one active hostname and a complete icon set are present.");
      return;
    }

    const payload = {
      enabled: !!pwa.enabled,
      app_name: appName,
      short_name: shortName,
      display_mode: pwa.display_mode || "standalone",
      theme_color: safeColor(pwa.theme_color),
      background_color: safeColor(pwa.background_color),
      start_url: startUrl,
      scope,
    };

    setPwaSaving(true);
    try {
      const res = await api.put("/pwa/settings", payload);
      setPwa({
        ...emptyPwaSettings(),
        ...(res.data || payload),
        icon_urls: res.data?.icon_urls || pwa.icon_urls || {},
      });
      alert("PWA settings saved.");
    } catch (e) {
      console.error("Failed to save PWA settings:", e?.response?.data || e?.message || e);
      alert(e?.response?.data?.detail || "Failed to save PWA settings.");
    } finally {
      setPwaSaving(false);
    }
  };

  const uploadPwaIcon = async (file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setPwaUploading(true);
    try {
      const res = await api.post("/pwa/icon", formData);
      setPwa({
        ...emptyPwaSettings(),
        ...(res.data || {}),
        icon_urls: res.data?.icon_urls || {},
      });
      alert("PWA icon set generated successfully.");
    } catch (e) {
      console.error("Failed to upload PWA icon:", e?.response?.data || e?.message || e);
      alert(e?.response?.data?.detail || "Failed to upload PWA icon.");
    } finally {
      setPwaUploading(false);
    }
  };

  const uploadPwaSplash = async (kind, file) => {
    if (!file || pwaSplashBusy) return;
    if (!['image/png', 'image/jpeg', 'image/webp'].includes(file.type)) {
      alert('Choose a PNG, JPEG or WebP image.');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      alert('Splash images must not exceed 8 MB.');
      return;
    }

    const data = new FormData();
    data.append('file', file);
    setPwaSplashBusy(kind);
    try {
      const response = await api.post(`/pwa/splash/${kind}`, data);
      setPwa({ ...emptyPwaSettings(), ...(response.data || {}) });
    } catch (error) {
      console.error(`Failed to upload PWA splash ${kind}:`, error?.response?.data || error);
      alert(error?.response?.data?.detail || `Failed to upload splash ${kind}.`);
    } finally {
      setPwaSplashBusy(null);
    }
  };

  const removePwaSplash = async (kind) => {
    if (pwaSplashBusy || !window.confirm(`Remove the splash ${kind} image?`)) return;
    setPwaSplashBusy(kind);
    try {
      const response = await api.delete(`/pwa/splash/${kind}`);
      setPwa({ ...emptyPwaSettings(), ...(response.data || {}) });
    } catch (error) {
      console.error(`Failed to remove PWA splash ${kind}:`, error?.response?.data || error);
      alert(error?.response?.data?.detail || `Failed to remove splash ${kind}.`);
    } finally {
      setPwaSplashBusy(null);
    }
  };

  const addPwaHostname = async () => {
    const hostname = newPwaHostname.trim();
    if (!hostname) {
      alert("Enter a hostname first.");
      return;
    }

    setPwaHostnameSaving(true);
    try {
      await api.post("/pwa/hostnames", {
        hostname,
        is_primary: !!newPwaHostnamePrimary,
        active: true,
      });
      setNewPwaHostname("");
      setNewPwaHostnamePrimary(false);
      await refreshPwaConfiguration();
    } catch (e) {
      console.error("Failed to add PWA hostname:", e?.response?.data || e?.message || e);
      alert(e?.response?.data?.detail || "Failed to add PWA hostname.");
    } finally {
      setPwaHostnameSaving(false);
    }
  };

  const updatePwaHostname = async (hostnameId, patch) => {
    setPwaHostnameSaving(true);
    try {
      await api.put(`/pwa/hostnames/${hostnameId}`, patch);
      await refreshPwaConfiguration();
    } catch (e) {
      console.error("Failed to update PWA hostname:", e?.response?.data || e?.message || e);
      alert(e?.response?.data?.detail || "Failed to update PWA hostname.");
    } finally {
      setPwaHostnameSaving(false);
    }
  };

  const deletePwaHostname = async (hostnameId, hostname) => {
    if (!window.confirm(`Remove PWA hostname ${hostname}?`)) return;

    setPwaHostnameSaving(true);
    try {
      await api.delete(`/pwa/hostnames/${hostnameId}`);
      await refreshPwaConfiguration();
    } catch (e) {
      console.error("Failed to remove PWA hostname:", e?.response?.data || e?.message || e);
      alert(e?.response?.data?.detail || "Failed to remove PWA hostname.");
    } finally {
      setPwaHostnameSaving(false);
    }
  };

  // ----- SharePoint handlers -----
  const setSpField = (key, value) => setSp((prev) => ({ ...prev, [key]: value }));

  const saveSharePoint = async () => {
    if (!tenantId) {
      alert("Tenant not resolved yet. Please try again.");
      return;
    }
    setSpSaving(true);

    const payload = {
      tenant_id: tenantId,
      enabled: !!sp.enabled,
      domain: sp.domain || "",
      site_directory: sp.site_directory || "",
      site_id: sp.site_id || "",
      clients_drive_id: sp.clients_drive_id || "",
      root_path: sp.root_path || "/",
      aad_tenant_id: sp.aad_tenant_id || "",
      client_id: sp.client_id || "",
      ...(sp.client_secret_plain ? { client_secret: sp.client_secret_plain } : {}),
    };

    try {
      let res;
      if (sp?.id) res = await api.put(`/sharepoint/settings/${sp.id}`, payload);
      else res = await api.post(`/sharepoint/settings`, payload);

      const data = res.data || {};
      setSp({
        ...emptySharePoint(tenantId),
        ...data,
        tenant_id: tenantId,
        access_token_masked: data.access_token_masked || null,
        client_secret_plain: "",
      });
      alert("SharePoint settings saved.");
    } catch (e) {
      console.error("Failed to save SharePoint settings:", e?.response?.data || e?.message || e);
      alert("Save failed.");
    } finally {
      setSpSaving(false);
    }
  };

  const handleTestSharePoint = async () => {
    if (!tenantId) {
      alert("Tenant not resolved yet. Please try again.");
      return;
    }
    setSpTesting(true);
    setSpTest(null);
    try {
      const res = await api.get("/sharepoint/settings/test", { params: { tenant_id: tenantId } });
      setSpTest(res.data || { ok: false, message: "Empty response" });
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Request failed";
      setSpTest({ ok: false, message: msg, error_detail: JSON.stringify(e?.response?.data || {}) });
    } finally {
      setSpTesting(false);
    }
  };

  // ----- Messaging handlers -----
  const setMessagingField = (key, value) =>
    setMessaging((prev) => ({
      ...prev,
      [key]: value,
    }));

  const handleSaveMessaging = async () => {
    if (!tenantId) {
      alert("Tenant not resolved yet. Please try again.");
      return;
    }

    const payload = {
      tenant_id: tenantId,
      sendgrid_from_name: messaging.sendgrid_from_name || "",
      sendgrid_from_email: messaging.sendgrid_from_email || "",
      reply_to_email: messaging.reply_to_email || "",
      whatsapp_from_number: messaging.whatsapp_from_number || "",
      email_dry_run: !!messaging.email_dry_run,
      enable_email: !!messaging.enable_email,
      enable_whatsapp: !!messaging.enable_whatsapp,
      enable_sms: !!messaging.enable_sms,
      enable_livechat: !!messaging.enable_livechat,
      send_usage_report: !!messaging.send_usage_report,
      usage_report_email: messaging.usage_report_email || "",
      sms_sender_id: messaging.sms_sender_id || "",
      sms_from_number: messaging.sms_from_number || "",
      whatsapp_business_name: messaging.whatsapp_business_name || "",
      whatsapp_template_sid: messaging.whatsapp_template_sid || "",
      ...(messaging.sendgrid_api_key_plain
        ? { sendgrid_api_key_plain: messaging.sendgrid_api_key_plain }
        : {}),
      ...(messaging.twilio_api_key_plain
        ? { twilio_api_key_plain: messaging.twilio_api_key_plain }
        : {}),
      ...(messaging.twilio_account_sid
        ? { twilio_account_sid: messaging.twilio_account_sid }
        : {}),
      ...(messaging.twilio_messaging_service_sid
        ? { twilio_messaging_service_sid: messaging.twilio_messaging_service_sid }
        : {}),
    };

    try {
      const res = await api.put("/messaging/settings", payload);
      const data = res.data || payload;

      setMessaging((prev) => ({
        ...prev,
        id: data.id ?? prev.id,
        tenant_id: data.tenant_id ?? tenantId,
        sendgrid_from_name: data.sendgrid_from_name || prev.sendgrid_from_name || "",
        sendgrid_from_email: data.sendgrid_from_email || prev.sendgrid_from_email || "",
        reply_to_email: data.reply_to_email || prev.reply_to_email || "",
        whatsapp_from_number:
          data.whatsapp_from_number || prev.whatsapp_from_number || "",
        email_dry_run:
          typeof data.email_dry_run === "boolean"
            ? data.email_dry_run
            : prev.email_dry_run,
        enable_email:
          typeof data.enable_email === "boolean"
            ? data.enable_email
            : prev.enable_email,
        enable_whatsapp:
          typeof data.enable_whatsapp === "boolean"
            ? data.enable_whatsapp
            : prev.enable_whatsapp,
        enable_sms:
          typeof data.enable_sms === "boolean"
            ? data.enable_sms
            : prev.enable_sms,
        enable_livechat:
          typeof data.enable_livechat === "boolean"
            ? data.enable_livechat
            : prev.enable_livechat,
        send_usage_report:
          typeof data.send_usage_report === "boolean"
            ? data.send_usage_report
            : prev.send_usage_report,
        usage_report_email:
          data.usage_report_email || prev.usage_report_email || "",
        sms_sender_id: data.sms_sender_id || prev.sms_sender_id || "",
        sms_from_number: data.sms_from_number || prev.sms_from_number || "",
        whatsapp_business_name:
          data.whatsapp_business_name || prev.whatsapp_business_name || "",
        whatsapp_template_sid:
          data.whatsapp_template_sid || prev.whatsapp_template_sid || "",
        sendgrid_api_key_masked:
          data.sendgrid_api_key_masked ?? prev.sendgrid_api_key_masked ?? null,
        twilio_api_key_masked:
          data.twilio_api_key_masked ?? prev.twilio_api_key_masked ?? null,
        twilio_account_sid_masked:
          data.twilio_account_sid_masked ??
          prev.twilio_account_sid_masked ??
          null,
        twilio_messaging_service_sid_masked:
          data.twilio_messaging_service_sid_masked ??
          prev.twilio_messaging_service_sid_masked ??
          null,
        sendgrid_api_key_plain: "",
        twilio_api_key_plain: "",
        twilio_account_sid: "",
        twilio_messaging_service_sid: "",
      }));

      alert("Messaging settings saved.");
    } catch (e) {
      console.error("Failed to save messaging settings:", e?.response?.data || e?.message || e);
      alert("Save failed.");
    }
  };

  const handleTestMessaging = async (channel) => {
    if (!tenantId) {
      alert("Tenant not resolved yet. Please try again.");
      return;
    }

    let url = "";
    let payload = {};

    if (channel === "email") {
      url = "/messaging/test/email";
      payload = {
        to:
          messaging.test_email ||
          messaging.usage_report_email ||
          messaging.sendgrid_from_email ||
          undefined,
        subject: "Vision Systems Messaging – Test Email",
      };
    } else if (channel === "whatsapp") {
      url = "/messaging/test/whatsapp";
      payload = {
        to: messaging.test_number || "",
        message: "Vision Systems - WhatsApp test message.",
      };
    } else if (channel === "sms") {
      url = "/messaging/test/sms";
      payload = {
        to: messaging.test_number || "",
        message: "Vision Systems - SMS test message.",
      };
    }

    try {
      const res = await api.post(url, payload, { params: { tenant_id: tenantId } });
      const data = res.data || {};

      const lines = [];
      lines.push(`Channel: ${channel.toUpperCase()}`);
      if (typeof data.dry_run === "boolean") {
        lines.push(`Mode: ${data.dry_run ? "DRY RUN" : "LIVE"}`);
      }
      if (data.to) lines.push(`To: ${data.to}`);
      if (data.from) lines.push(`From: ${data.from}`);
      if (data.subject) lines.push(`Subject: ${data.subject}`);
      if (data.preview) lines.push(`Message: ${data.preview}`);
      if (data.message && !data.preview && !data.subject) {
        lines.push(`Message: ${data.message}`);
      }
      if (typeof data.sent === "boolean") {
        lines.push(`Sent: ${data.sent ? "Yes" : "No"}`);
      }
      if (data.reason) lines.push(`Reason: ${data.reason}`);
      if (data.provider_status) {
        lines.push(`Provider status: ${data.provider_status}`);
      }

      alert(lines.join("\n") || "Test completed.");
    } catch (e) {
      console.error("Test messaging failed:", e?.response?.data || e?.message || e);
      const detail =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.message ||
        "Internal Server Error";
      alert(`Test ${channel} failed:\n${detail}`);
    }
  };

  // ----- Weather handlers -----
  const setWeatherField = (key, value) =>
    setWeather((prev) => ({
      ...prev,
      [key]: value,
    }));

  const saveWeather = async () => {
    if (!tenantId) {
      alert("Tenant not resolved yet. Please try again.");
      return;
    }

    const ttlRaw = typeof weather.cache_ttl_seconds === "string"
      ? parseInt(weather.cache_ttl_seconds, 10)
      : Number(weather.cache_ttl_seconds);

    const ttl = Number.isFinite(ttlRaw) ? ttlRaw : 0;
    if (ttl < 0 || ttl > 86400) {
      alert("Cache TTL must be between 0 and 86400 seconds.");
      return;
    }

    const payload = {
      enable_weather: !!weather.enable_weather,
      default_source: (weather.default_source || "BD1").trim(),
      cache_ttl_seconds: ttl,
      ...(weather.metoffice_global_spot_api_key_plain
        ? { metoffice_global_spot_api_key: weather.metoffice_global_spot_api_key_plain.trim() }
        : {}),
      ...(weather.metoffice_bpf_api_key_plain
        ? { metoffice_bpf_api_key: weather.metoffice_bpf_api_key_plain.trim() }
        : {}),
    };

    setWeatherSaving(true);
    try {
      const res = await api.patch("/weather/settings", payload);
      const data = res.data || {};
      setWeather((prev) => ({
        ...prev,
        tenant_id: data.tenant_id ?? prev.tenant_id ?? tenantId,
        enable_weather: typeof data.enable_weather === "boolean" ? data.enable_weather : prev.enable_weather,
        default_source: data.default_source || prev.default_source || "BD1",
        cache_ttl_seconds:
          typeof data.cache_ttl_seconds === "number"
            ? data.cache_ttl_seconds
            : prev.cache_ttl_seconds,
        metoffice_global_spot_api_key_masked:
          data.metoffice_global_spot_api_key_masked ?? prev.metoffice_global_spot_api_key_masked ?? null,
        metoffice_bpf_api_key_masked:
          data.metoffice_bpf_api_key_masked ?? prev.metoffice_bpf_api_key_masked ?? null,
        metoffice_global_spot_api_key_plain: "",
        metoffice_bpf_api_key_plain: "",
      }));
      alert("Weather settings saved.");
    } catch (e) {
      console.error("Failed to save weather settings:", e?.response?.data || e?.message || e);
      const detail = e?.response?.data?.detail || e?.message || "Save failed.";
      alert(detail);
    } finally {
      setWeatherSaving(false);
    }
  };

  const handleTestWeather = async () => {
    if (!tenantId) {
      alert("Tenant not resolved yet. Please try again.");
      return;
    }
    if (!weather.enable_weather) {
      alert("Weather is disabled. Enable it to test.");
      return;
    }

    setWeatherTesting(true);
    setWeatherTest(null);

    try {
      const res = await api.get("/weather/test");
      setWeatherTest(res.data || { ok: true });
    } catch (e) {
      console.error("Test weather failed:", e?.response?.data || e?.message || e);
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.message ||
        "Request failed";
      setWeatherTest({ ok: false, message: msg, error_detail: JSON.stringify(e?.response?.data || {}) });
    } finally {
      setWeatherTesting(false);
    }
  };

  // ----- Traffic handlers -----
  const setTrafficField = (key, value) =>
    setTraffic((prev) => ({
      ...prev,
      [key]: value,
    }));

  const saveTraffic = async () => {
    if (!tenantId) {
      alert("Tenant not resolved yet. Please try again.");
      return;
    }

    const zoom = Number(traffic.zoom);
    const cacheTtl = Number(traffic.cache_ttl_seconds);
    const connectTimeout = Number(traffic.connect_timeout_seconds);
    const readTimeout = Number(traffic.read_timeout_seconds);
    const maxRequests = Number(traffic.max_requests_per_job_check);

    if (!Number.isFinite(zoom) || zoom < 1 || zoom > 22) {
      alert("Zoom must be between 1 and 22.");
      return;
    }
    if (!Number.isFinite(cacheTtl) || cacheTtl < 0) {
      alert("Cache TTL must be 0 or greater.");
      return;
    }
    if (!Number.isFinite(connectTimeout) || connectTimeout < 1 || connectTimeout > 60) {
      alert("Connect timeout must be between 1 and 60 seconds.");
      return;
    }
    if (!Number.isFinite(readTimeout) || readTimeout < 1 || readTimeout > 120) {
      alert("Read timeout must be between 1 and 120 seconds.");
      return;
    }
    if (!Number.isFinite(maxRequests) || maxRequests < 1 || maxRequests > 100) {
      alert("Max requests per job check must be between 1 and 100.");
      return;
    }

    const payload = {
      provider: "tomtom",
      is_enabled: !!traffic.is_enabled,
      api_key: traffic.api_key || null,
      base_url:
        traffic.base_url ||
        "https://api.tomtom.com/traffic/services/4/flowSegmentData/absolute/10/json",
      unit: traffic.unit || "KMPH",
      zoom,
      cache_ttl_seconds: cacheTtl,
      connect_timeout_seconds: connectTimeout,
      read_timeout_seconds: readTimeout,
      persist_snapshots: !!traffic.persist_snapshots,
      max_requests_per_job_check: maxRequests,
      notes: traffic.notes || null,
    };

    setTrafficSaving(true);
    try {
      const res = await api.put("/traffic/settings", payload);
      const data = res.data || {};
      setTraffic({
        ...emptyTraffic(),
        ...data,
        tenant_id: data.tenant_id ?? tenantId,
        provider: data.provider || "tomtom",
        unit: data.unit || "KMPH",
        api_key: data.api_key || "",
        base_url:
          data.base_url ||
          "https://api.tomtom.com/traffic/services/4/flowSegmentData/absolute/10/json",
        notes: data.notes || "",
      });
      alert("Traffic settings saved.");
    } catch (e) {
      console.error("Failed to save traffic settings:", e?.response?.data || e?.message || e);
      const detail = e?.response?.data?.detail || e?.message || "Save failed.";
      alert(detail);
    } finally {
      setTrafficSaving(false);
    }
  };

  const handleTestTraffic = async () => {
    if (!tenantId) {
      alert("Tenant not resolved yet. Please try again.");
      return;
    }
    if (!traffic.is_enabled) {
      alert("Live traffic is disabled. Enable it to test.");
      return;
    }

    setTrafficTesting(true);
    setTrafficTest(null);

    try {
      const res = await api.get("/traffic/intelligence", {
        params: {
          lat: 52.240477,
          lon: -0.902656,
          job_type_label: "Drone Survey",
          persist: false,
          cache_bust: true,
        },
      });
      setTrafficTest(res.data || { ok: true });
    } catch (e) {
      console.error("Test traffic failed:", e?.response?.data || e?.message || e);
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.message ||
        "Request failed";
      setTrafficTest({ ok: false, message: msg, error_detail: JSON.stringify(e?.response?.data || {}) });
    } finally {
      setTrafficTesting(false);
    }
  };

  // ---------- Fonts ----------
  const googleFonts = [
    "Roboto",
    "Open Sans",
    "Lato",
    "Montserrat",
    "Poppins",
    "Nunito",
    "Oswald",
    "Raleway",
    "Merriweather",
    "Rubik",
    "Work Sans",
    "Inter",
    "Quicksand",
    "Source Sans Pro",
  ];
  const fontOptions = ["Arial", "Courier New", "Verdana", "Tahoma", "Georgia", ...googleFonts];

  // ---------- Render ----------
  return (
    <div
      className={isMobile ? undefined : "p-6"}
      style={{
        backgroundColor: "#f0f0f0",
        minHeight: isMobile ? "100%" : "100vh",
        paddingLeft: isMobile ? 12 : 24,
        paddingRight: isMobile ? 12 : 24,
        paddingTop: isMobile ? 8 : undefined,
        paddingBottom: isMobile ? 16 : undefined,
        boxSizing: "border-box",
        overflowX: "hidden",
        fontFamily: theme.font_body,
      }}
    >
      <h2
        className={isMobile ? undefined : "text-2xl font-semibold"}
        style={{
          fontFamily: theme.font_header,
          marginTop: 0,
          marginBottom: isMobile ? 12 : "20px",
          fontSize: isMobile ? "1.35rem" : undefined,
          fontWeight: 600,
        }}
      >
        System Settings
      </h2>

      {/* Tabs / mobile section selector */}
      {isMobile ? (
        <div
          style={{
            background: "#fff",
            border: "1px solid #ddd",
            borderRadius: 10,
            padding: 10,
            marginBottom: 10,
          }}
        >
          <label
            htmlFor="system-mobile-section"
            style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 5 }}
          >
            Settings Section
          </label>
          <select
            id="system-mobile-section"
            value={activeTab}
            onChange={(e) => setActiveTab(e.target.value)}
            style={{
              width: "100%",
              height: 46,
              boxSizing: "border-box",
              borderRadius: 8,
              border: "1px solid #bbb",
              background: "#fff",
              padding: "8px 38px 8px 10px",
              fontSize: 16,
              fontWeight: 700,
              color: "#333",
            }}
          >
            <option value="whitelabel">White Label</option>
            <option value="companyinfo">Company Info</option>
            <option value="integrations">Integrations</option>
            <option value="policies">Expense Policies</option>
            <option value="resources">Resources</option>
            <option value="records">Records Management</option>
            <option value="licence">Licence Manager</option>
          </select>
        </div>
      ) : (
        <div style={{ display: "flex", gap: "10px", marginBottom: 0 }}>
          {[
            { key: "whitelabel", label: "White Label" },
            { key: "companyinfo", label: "Company Info" },
            { key: "integrations", label: "Integrations" },
            { key: "policies", label: "Expense Policies" },
            { key: "resources", label: "Resources" },
            { key: "records", label: "Records Management" },
            { key: "licence", label: "Licence Manager" },
          ].map((tab) => (
            <div
              key={tab.key}
              style={{
                padding: "10px 20px",
                cursor: "pointer",
                backgroundColor: activeTab === tab.key ? theme.primary_color : "#f7f7f7",
                color: activeTab === tab.key ? "#fff" : "#333",
                border: "1px solid #ccc",
                borderBottom: activeTab === tab.key ? "1px solid #fff" : "1px solid #ccc",
                borderTopLeftRadius: "8px",
                borderTopRightRadius: "8px",
                fontWeight: "bold",
                marginBottom: "-1px",
              }}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.label}
            </div>
          ))}
        </div>
      )}

      {/* White Label */}
      {activeTab === "whitelabel" && (
        <div style={{ ...sectionStyle }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
            <div style={{ flex: "1 1 48%", minWidth: isMobile ? 0 : 300, width: isMobile ? "100%" : undefined, display: "flex", flexDirection: "column", gap: 10 }}>
              <h4>Corporate Identity</h4>
              <label style={labelStyle}>Login Page Logo (500x500 PNG)</label>
              {loginPreview ? (
                <img
                  src={loginPreview}
                  alt="Login Preview"
                  style={{
                    border: "1px dashed #bbb",
                    width: 100,
                    height: 100,
                    borderRadius: 8,
                    marginTop: 6,
                    marginBottom: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <div
                  style={{
                    border: "1px dashed #bbb",
                    width: 100,
                    height: 100,
                    borderRadius: 8,
                    marginTop: 6,
                    marginBottom: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  500x500
                </div>
              )}
              <input
                type="file"
                accept="image/png"
                style={{ ...inputStyle, height: 48 }}
                onChange={(e) => handleFileChange("logo_login_url", e.target.files[0])}
              />

              <label style={{ ...labelStyle, marginTop: 20 }}>Top Navigation Bar Logo (260x100 PNG)</label>
              {headerPreview ? (
                <img
                  src={headerPreview}
                  alt="Header Preview"
                  style={{
                    border: "1px dashed #bbb",
                    width: isMobile ? "100%" : 260,
                    maxWidth: 260,
                    height: 100,
                    borderRadius: 8,
                    marginTop: 6,
                    marginBottom: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <div
                  style={{
                    border: "1px dashed #bbb",
                    width: 64,
                    height: 64,
                    borderRadius: 8,
                    marginTop: 6,
                    marginBottom: 10,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  64x64
                </div>
              )}
              <input
                type="file"
                accept="image/png"
                style={{ ...inputStyle, height: 48 }}
                onChange={(e) => handleFileChange("logo_header_url", e.target.files[0])}
              />

              <label style={{ ...labelStyle, marginTop: 20 }}>Company Name</label>
              <input
                type="text"
                value={form.client_name}
                onChange={(e) => handleChange("client_name", e.target.value)}
                style={{ ...inputStyle, maxWidth: 300, height: 34 }}
              />
            </div>

            <div style={{ flex: "1 1 48%", minWidth: isMobile ? 0 : 300, width: isMobile ? "100%" : undefined, display: "flex", flexDirection: "column", gap: 10 }}>
              <h4>User Interface</h4>

              <div style={{ display: "flex", flexDirection: "column", marginTop: 20 }}>
                <label style={labelStyle}>Primary Colour + Hover</label>
                <div style={{ display: "flex", gap: isMobile ? 10 : 24, flexWrap: isMobile ? "wrap" : "nowrap" }}>
                  <ColorInput value={form.primary_color} onChange={(v) => handleChange("primary_color", v)} />
                  <ColorInput value={form.primary_hover} onChange={(v) => handleChange("primary_hover", v)} />
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", marginTop: 20 }}>
                <label style={labelStyle}>Secondary Colour + Hover</label>
                <div style={{ display: "flex", gap: isMobile ? 10 : 24, flexWrap: isMobile ? "wrap" : "nowrap" }}>
                  <ColorInput value={form.secondary_color} onChange={(v) => handleChange("secondary_color", v)} />
                  <ColorInput value={form.secondary_hover} onChange={(v) => handleChange("secondary_hover", v)} />
                </div>
              </div>

              <div style={{ display: "flex", flexDirection: "column", marginTop: 20 }}>
                <label style={labelStyle}>Accent Colour + Hover</label>
                <div style={{ display: "flex", gap: isMobile ? 10 : 24, flexWrap: isMobile ? "wrap" : "nowrap" }}>
                  <ColorInput value={form.accent_color} onChange={(v) => handleChange("accent_color", v)} />
                  <ColorInput value={form.accent_hover} onChange={(v) => handleChange("accent_hover", v)} />
                </div>
              </div>

              <label style={{ ...labelStyle, marginTop: 20 }}>Primary Font</label>
              <select
                value={form.font_header}
                onChange={(e) => handleChange("font_header", e.target.value)}
                style={{ ...selectStyle, maxWidth: 300 }}
              >
                {fontOptions.map((font) => (
                  <option key={font}>{font}</option>
                ))}
              </select>

              <label style={{ ...labelStyle, marginTop: 20 }}>Secondary Font</label>
              <select
                value={form.font_body}
                onChange={(e) => handleChange("font_body", e.target.value)}
                style={{ ...selectStyle, maxWidth: 300 }}
              >
                {fontOptions.map((font) => (
                  <option key={font}>{font}</option>
                ))}
              </select>

              <div style={{ display: "flex", gap: isMobile ? 16 : "30px", marginTop: "20px", flexDirection: isMobile ? "column" : "row" }}>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>
                    <strong>General Buttons</strong>
                  </label>
                  {["normal", "hover", "click"].map((state) => (
                    <div key={state} style={{ marginTop: "8px" }}>
                      <span style={{ marginRight: 10, width: 70, display: "inline-block" }}>{state}</span>
                      <ColorInput
                        value={form.button_general[state]}
                        onChange={(val) => handleNestedChange("button_general", state, val)}
                      />
                    </div>
                  ))}
                </div>
                <div style={{ flex: 1 }}>
                  <label style={labelStyle}>
                    <strong>Action Buttons</strong>
                  </label>
                  {["normal", "hover", "click"].map((state) => (
                    <div key={state} style={{ marginTop: "8px" }}>
                      <span style={{ marginRight: 10, width: 70, display: "inline-block" }}>{state}</span>
                      <ColorInput
                        value={form.button_action[state]}
                        onChange={(val) => handleNestedChange("button_action", state, val)}
                      />
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          <div style={{ width: "100%", display: "flex", justifyContent: "flex-end", marginTop: 30 }}>
            <button
              onClick={handleSaveBranding}
              onMouseEnter={() => setIsHovering(true)}
              onMouseLeave={() => {
                setIsHovering(false);
                setIsClicking(false);
              }}
              onMouseDown={() => setIsClicking(true)}
              onMouseUp={() => setIsClicking(false)}
              style={{
                padding: "10px 20px",
                backgroundColor: getButtonColor() || theme.secondary_color,
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                fontFamily: theme.font_body,
                cursor: "pointer",
                fontWeight: "bold",
                transition: "background-color 0.2s ease",
              }}
            >
              Save Configuration
            </button>
          </div>
        </div>
      )}

      {/* Company Info */}
      {activeTab === "companyinfo" && (
        <div style={{ ...sectionStyle }}>
          <div style={{ maxWidth: 320 }}>
            <label style={labelStyle}>Tenant ID</label>
            <input
              type="number"
              value={tenantId ?? ""}
              readOnly
              disabled
              style={{ ...inputStyle, backgroundColor: "#f7f7f7", cursor: "not-allowed" }}
              title="Managed in Super Admin (licensing)"
            />
            <div style={{ color: "#666", fontStyle: "italic", marginTop: 6 }}>
              {orgLoading ? "Loading organisation..." : org?.id ? `Organisation ID: ${org.id}` : "No organisation saved yet"}
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 20 }}>
            <div>
              <label style={labelStyle}>Company Name</label>
              <input
                type="text"
                value={org.company_name}
                onChange={(e) => setOrgField("company_name", e.target.value)}
                style={inputStyle}
              />

              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 16, marginTop: 12 }}>
                <div>
                  <label style={labelStyle}>Company Number</label>
                  <input
                    type="text"
                    value={org.company_number || ""}
                    onChange={(e) => setOrgField("company_number", e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>SIC Code</label>
                  <input
                    type="text"
                    value={org.sic_code || ""}
                    onChange={(e) => setOrgField("sic_code", e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>VAT Number</label>
                  <input
                    type="text"
                    value={org.vat_number || ""}
                    onChange={(e) => setOrgField("vat_number", e.target.value)}
                    style={inputStyle}
                  />
                </div>
              </div>

              <div style={{ marginTop: 12 }}>
                <label style={labelStyle}>Email</label>
                <input
                  type="email"
                  value={org.email || ""}
                  onChange={(e) => setOrgField("email", e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 16, marginTop: 12 }}>
                <div>
                  <label style={labelStyle}>Phone Number</label>
                  <input
                    type="text"
                    value={org.telephone || ""}
                    onChange={(e) => setOrgField("telephone", e.target.value)}
                    style={{ ...inputStyle }}
                    placeholder="+44 20 1234 5678"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Trading Since</label>
                  <input
                    type="date"
                    value={org.trading_since || ""}
                    onChange={(e) => setOrgField("trading_since", e.target.value)}
                    style={{ ...inputStyle }}
                  />
                </div>
              </div>

              <div style={{ marginTop: 12 }}>
                <label style={labelStyle}>Address Line 1</label>
                <input
                  type="text"
                  value={org.address_line_1 || ""}
                  onChange={(e) => setOrgField("address_line_1", e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Address Line 2</label>
                <input
                  type="text"
                  value={org.address_line_2 || ""}
                  onChange={(e) => setOrgField("address_line_2", e.target.value)}
                  style={inputStyle}
                />
              </div>
              <div>
                <label style={labelStyle}>Address Line 3</label>
                <input
                  type="text"
                  value={org.address_line_3 || ""}
                  onChange={(e) => setOrgField("address_line_3", e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr", gap: 16, marginTop: 12 }}>
                <div>
                  <label style={labelStyle}>City</label>
                  <input
                    type="text"
                    value={org.city || ""}
                    onChange={(e) => setOrgField("city", e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>County</label>
                  <input
                    type="text"
                    value={org.county || ""}
                    onChange={(e) => setOrgField("county", e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Postcode</label>
                  <input
                    type="text"
                    value={org.postcode || ""}
                    onChange={(e) => setOrgField("postcode", e.target.value)}
                    style={inputStyle}
                  />
                </div>
              </div>
            </div>

            <div>
              <div style={{ marginTop: 0 }}>
                <label style={labelStyle}>Business Hours</label>
                <div style={{ overflowX: "auto" }}>
                  <table className="w-full text-sm" style={{ borderCollapse: "collapse", width: "100%" }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: "left", padding: 8 }}>Day</th>
                        <th style={{ textAlign: "left", padding: 8 }}>Closed</th>
                        <th style={{ textAlign: "left", padding: 8 }}>Open</th>
                        <th style={{ textAlign: "left", padding: 8 }}>Close</th>
                      </tr>
                    </thead>
                    <tbody>
                      {org.business_hours.map((row, idx) => (
                        <tr key={row.day} style={{ borderTop: "1px solid #eee" }}>
                          <td style={{ padding: 8, fontWeight: 600 }}>{row.day}</td>
                          <td style={{ padding: 8 }}>
                            <input
                              type="checkbox"
                              checked={!!row.closed}
                              onChange={(e) => setHour(idx, { closed: e.target.checked })}
                            />
                          </td>
                          <td style={{ padding: 8 }}>
                            <input
                              type="time"
                              value={row.open || "09:00"}
                              disabled={row.closed}
                              onChange={(e) => setHour(idx, { open: e.target.value })}
                              style={{ ...inputStyle, maxWidth: 140 }}
                            />
                          </td>
                          <td style={{ padding: 8 }}>
                            <input
                              type="time"
                              value={row.close || "17:00"}
                              disabled={row.closed}
                              onChange={(e) => setHour(idx, { close: e.target.value })}
                              style={{ ...inputStyle, maxWidth: 140 }}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div>
            <AccordionHeader
              title="Public Holidays Calendar"
              open={holidaysOpen}
              onToggle={() => setHolidaysOpen((v) => !v)}
            />
            {holidaysOpen && (
              <div style={{ marginTop: 12, padding: 12, border: "1px solid #eee", borderRadius: 8 }}>
                <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 12 }}>
                  <input
                    type="date"
                    onChange={(e) => addHoliday(e.target.value)}
                    style={{ ...inputStyle, maxWidth: 220 }}
                  />
                  <span style={{ color: "#666" }}>Select a date to add it</span>
                </div>
                {org.public_holidays.length === 0 ? (
                  <div style={{ color: "#666", fontStyle: "italic" }}>No public holidays added.</div>
                ) : (
                  <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
                    {org.public_holidays.map((d, idx) => (
                      <li
                        key={`${d}-${idx}`}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "6px 8px",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        <span>{d}</span>
                        <button onClick={() => removeHoliday(idx)} style={generalBtn} title="Remove">
                          Remove
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>

          <div>
            <AccordionHeader
              title="Custom Closures (e.g. Staff Training)"
              open={closuresOpen}
              onToggle={() => setClosuresOpen((v) => !v)}
            />
            {closuresOpen && (
              <div style={{ marginTop: 12, padding: 12, border: "1px solid #eee", borderRadius: 8 }}>
                <div style={{ marginBottom: 12 }}>
                  <button onClick={addClosure} style={generalBtn}>
                    Add Closure
                  </button>
                </div>
                {org.custom_closures.length === 0 ? (
                  <div style={{ color: "#666", fontStyle: "italic" }}>No custom closures added.</div>
                ) : (
                  <table className="w-full text-sm" style={{ borderCollapse: "collapse", width: "100%" }}>
                    <thead>
                      <tr>
                        <th style={{ textAlign: "left", padding: 8 }}>Date</th>
                        <th style={{ textAlign: "left", padding: 8 }}>Period</th>
                        <th style={{ textAlign: "left", padding: 8 }}>Note</th>
                        <th style={{ textAlign: "center", padding: 8 }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {org.custom_closures.map((c, idx) => (
                        <tr key={idx} style={{ borderTop: "1px solid #eee" }}>
                          <td style={{ padding: 8 }}>
                            <input
                              type="date"
                              value={c.date || ""}
                              onChange={(e) => updateClosure(idx, { date: e.target.value })}
                              style={{ ...inputStyle, maxWidth: 220 }}
                            />
                          </td>
                          <td style={{ padding: 8 }}>
                            <select
                              value={c.period || "FULL"}
                              onChange={(e) => updateClosure(idx, { period: e.target.value })}
                              style={{ ...selectStyle, maxWidth: 160 }}
                            >
                              <option value="FULL">Full Day</option>
                              <option value="AM">AM</option>
                              <option value="PM">PM</option>
                            </select>
                          </td>
                          <td style={{ padding: 8 }}>
                            <input
                              type="text"
                              value={c.note || ""}
                              onChange={(e) => updateClosure(idx, { note: e.target.value })}
                              style={inputStyle}
                              placeholder="Reason"
                            />
                          </td>
                          <td style={{ padding: 8, textAlign: "center" }}>
                            <button
                              onClick={() => {
                                if (window.confirm("Remove this closure?")) removeClosure(idx);
                              }}
                              style={dangerBtn}
                            >
                              Remove
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>

          <div style={{ width: "100%", display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
            <button
              onClick={saveOrganisation}
              disabled={orgSaving || orgLoading || !tenantId || !org.company_name}
              style={{
                padding: "10px 20px",
                backgroundColor: "var(--btn-action-normal, #1f7a8c)",
                color: "#fff",
                border: "none",
                borderRadius: "8px",
                fontFamily: theme.font_body,
                cursor: orgSaving ? "progress" : "pointer",
                fontWeight: "bold",
                opacity: orgSaving ? 0.8 : 1,
              }}
              title={!org.company_name ? "Company Name is required" : "Save Organisation"}
            >
              {orgSaving ? "Saving..." : org?.id ? "Save Changes" : "Create Organisation"}
            </button>
          </div>
        </div>
      )}

      {activeTab === "companyinfo" && pwaAllowed === true && (
        <div style={{ ...sectionStyle, marginTop: 16 }}>
          <div>
            <h3 style={{ margin: 0, fontFamily: theme.font_header }}>PWA Configuration</h3>
            <div style={{ color: "#666", marginTop: 6, lineHeight: 1.5 }}>
              Controls the installable Vision Systems application identity for this tenant.
              Hostname resolution is used only for public PWA branding; authenticated tenant authority remains server-side.
            </div>
          </div>

          {pwaLoading ? (
            <div style={{ color: "#666", fontStyle: "italic" }}>Loading PWA configuration...</div>
          ) : (
            <>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))",
                  gap: 10,
                }}
              >
                {[
                  ["Icon Set", pwa.has_icons],
                  ["Active Hostname", pwa.has_active_hostname],
                  ["Ready to Enable", pwa.ready_to_enable],
                ].map(([label, ok]) => (
                  <div
                    key={label}
                    style={{
                      border: "1px solid #ddd",
                      borderRadius: 8,
                      padding: 10,
                      background: ok ? "#f4fbf6" : "#fafafa",
                    }}
                  >
                    <div style={{ fontSize: 12, color: "#666", fontWeight: 700 }}>{label}</div>
                    <div style={{ marginTop: 4, fontWeight: 700 }}>{ok ? "Ready" : "Required"}</div>
                  </div>
                ))}
              </div>

              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 20 }}>
                <div>
                  <label style={labelStyle}>App Name</label>
                  <input
                    type="text"
                    value={pwa.app_name || ""}
                    maxLength={120}
                    onChange={(e) => setPwaField("app_name", e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Short Name</label>
                  <input
                    type="text"
                    value={pwa.short_name || ""}
                    maxLength={32}
                    onChange={(e) => setPwaField("short_name", e.target.value)}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Display Mode</label>
                  <select
                    value={pwa.display_mode || "standalone"}
                    onChange={(e) => setPwaField("display_mode", e.target.value)}
                    style={selectStyle}
                  >
                    <option value="standalone">Standalone</option>
                    <option value="fullscreen">Fullscreen</option>
                    <option value="minimal-ui">Minimal UI</option>
                    <option value="browser">Browser</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Start URL</label>
                  <input
                    type="text"
                    value={pwa.start_url || ""}
                    onChange={(e) => setPwaField("start_url", e.target.value)}
                    style={inputStyle}
                    placeholder="/dashboard"
                  />
                </div>
                <div>
                  <label style={labelStyle}>Scope</label>
                  <input
                    type="text"
                    value={pwa.scope || ""}
                    onChange={(e) => setPwaField("scope", e.target.value)}
                    style={inputStyle}
                    placeholder="/"
                  />
                </div>
                <div style={{ display: "flex", alignItems: "end", minHeight: isMobile ? 54 : undefined }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 700 }}>
                    <input
                      type="checkbox"
                      checked={!!pwa.enabled}
                      disabled={!pwa.enabled && !pwa.ready_to_enable}
                      onChange={(e) => setPwaField("enabled", e.target.checked)}
                    />
                    Enable PWA
                  </label>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 20 }}>
                <div>
                  <label style={labelStyle}>Theme Colour</label>
                  <ColorInput value={pwa.theme_color || "#172d41"} onChange={(v) => setPwaField("theme_color", v)} />
                </div>
                <div>
                  <label style={labelStyle}>Background Colour</label>
                  <ColorInput value={pwa.background_color || "#ffffff"} onChange={(v) => setPwaField("background_color", v)} />
                </div>
              </div>

              <div style={{ borderTop: "1px solid #eee", paddingTop: 14 }}>
                <h4 style={{ margin: 0, fontFamily: theme.font_header }}>Application Icon</h4>
                <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", gap: 16, marginTop: 10, alignItems: isMobile ? "stretch" : "center" }}>
                  {pwa.icon_urls?.["192_any"] ? (
                    <img
                      src={pwa.icon_urls["192_any"]}
                      alt="Current PWA icon"
                      style={{ width: 96, height: 96, borderRadius: 18, border: "1px solid #ddd", objectFit: "cover" }}
                    />
                  ) : (
                    <div
                      style={{
                        width: 96,
                        height: 96,
                        borderRadius: 18,
                        border: "1px dashed #bbb",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "#666",
                      }}
                    >
                      No icon
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    <input
                      type="file"
                      accept="image/png"
                      disabled={pwaUploading}
                      style={{ ...inputStyle, height: 48 }}
                      onChange={async (e) => {
                        const file = e.target.files?.[0];
                        await uploadPwaIcon(file);
                        e.target.value = "";
                      }}
                    />
                    <div style={{ color: "#666", fontSize: 12, marginTop: 6 }}>
                      Upload a square PNG. Vision generates 192×192 and 512×512 normal and maskable icons.
                      {pwa.icon_version > 0 ? ` Current icon version: ${pwa.icon_version}.` : ""}
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ borderTop: "1px solid #eee", paddingTop: 14 }}>
                <h4 style={{ margin: 0, fontFamily: theme.font_header }}>PWA Startup Screen</h4>
                <p style={{ color: "#666", fontSize: 13, marginTop: 6 }}>
                  The splash background and transparent logo are separate assets. They are composed
                  when the application starts; the installed application icon remains unchanged.
                  This preview represents the future in-app startup screen, not the operating system's native splash.
                </p>
                <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 18, alignItems: "start" }}>
                  {[
                    { kind: "logo", title: "Splash Screen Logo", url: pwa.splash_logo_url, exists: pwa.has_splash_logo, version: pwa.splash_logo_version, hint: "Use a transparent PNG or WebP for best results." },
                    { kind: "background", title: "Splash Background Image", url: pwa.splash_background_url, exists: pwa.has_splash_background, version: pwa.splash_background_version, hint: "Portrait artwork recommended. The image is cropped to cover the screen." },
                  ].map((item) => (
                    <div key={item.kind} style={{ border: "1px solid #ddd", borderRadius: 10, padding: 14, minWidth: 0 }}>
                      <label style={labelStyle}>{item.title}</label>
                      <div style={{ display: "flex", gap: 12, alignItems: "center", marginBottom: 10 }}>
                        <div style={{ width: 88, height: 88, flexShrink: 0, border: "1px dashed #bbb", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", background: item.kind === "logo" ? "#243647" : (pwa.background_color || "#ffffff") }}>
                          {item.exists && item.url ? (
                            <img src={item.url} alt={item.title} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                          ) : (
                            <span style={{ color: item.kind === "logo" ? "#eee" : "#555", fontSize: 12 }}>Not uploaded</span>
                          )}
                        </div>
                        <div style={{ color: "#666", fontSize: 12, minWidth: 0 }}>
                          {item.hint}
                          {item.exists ? <div style={{ marginTop: 6 }}>Version {item.version}</div> : null}
                        </div>
                      </div>
                      <input
                        type="file"
                        accept="image/png,image/jpeg,image/webp"
                        disabled={!!pwaSplashBusy || pwaSaving}
                        style={{ ...inputStyle, height: 48, width: "100%", maxWidth: "100%" }}
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          e.target.value = "";
                          await uploadPwaSplash(item.kind, file);
                        }}
                      />
                      {item.exists && (
                        <button type="button" onClick={() => removePwaSplash(item.kind)} disabled={!!pwaSplashBusy || pwaSaving}
                          style={{ ...dangerBtn, marginTop: 10, opacity: pwaSplashBusy || pwaSaving ? 0.6 : 1 }}>
                          Remove {item.kind === "logo" ? "Logo" : "Background"}
                        </button>
                      )}
                      {pwaSplashBusy === item.kind && <div style={{ fontSize: 12, color: "#666", marginTop: 6 }}>Processing image...</div>}
                    </div>
                  ))}
                </div>
                <h4 style={{ margin: "18px 0 9px", fontFamily: theme.font_header }}>Combined Startup Preview</h4>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 18, alignItems: "flex-start" }}>
                  <div style={{ width: "min(100%, 260px)", aspectRatio: "9 / 19.5", position: "relative", borderRadius: 22, border: "6px solid #202020", overflow: "hidden", backgroundColor: pwa.background_color || "#ffffff", backgroundImage: pwa.has_splash_background && pwa.splash_background_url ? `url("${pwa.splash_background_url}")` : "none", backgroundSize: "cover", backgroundPosition: "center", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {pwa.has_splash_logo && pwa.splash_logo_url ? (
                      <img src={pwa.splash_logo_url} alt="Composited splash logo" style={{ width: "72%", maxHeight: "44%", objectFit: "contain", position: "relative" }} />
                    ) : (
                      <span style={{ color: pwa.has_splash_background ? "#fff" : "#555", fontSize: 13, textAlign: "center", padding: 12, background: pwa.has_splash_background ? "rgba(0,0,0,.4)" : "transparent" }}>Upload a logo to complete the preview</span>
                    )}
                  </div>
                  <div style={{ flex: "1 1 190px", color: "#666", fontSize: 12, lineHeight: 1.6 }}>
                    <div>Background: covers the viewport and may crop on different screens.</div>
                    <div>Logo: scales to fit without distortion.</div>
                    <div>Fallback: configured PWA Background Colour.</div>
                    <div>Upload and removal take effect independently; no Save Settings action is required for images.</div>
                  </div>
                </div>
              </div>

              <div style={{ borderTop: "1px solid #eee", paddingTop: 14 }}>
                <h4 style={{ margin: 0, fontFamily: theme.font_header }}>Hostnames</h4>
                <div style={{ color: "#666", fontSize: 13, marginTop: 6 }}>
                  Add each hostname through which this tenant can be installed. Use the public production hostname as primary; localhost may remain as a development alias.
                </div>

                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: isMobile ? "1fr" : "minmax(220px, 1fr) auto auto",
                    gap: 10,
                    alignItems: "end",
                    marginTop: 10,
                  }}
                >
                  <div>
                    <label style={labelStyle}>New Hostname</label>
                    <input
                      type="text"
                      value={newPwaHostname}
                      onChange={(e) => setNewPwaHostname(e.target.value)}
                      style={inputStyle}
                      placeholder="vision.example.co.uk"
                    />
                  </div>
                  <label style={{ display: "flex", gap: 8, alignItems: "center", minHeight: 34, fontWeight: 600 }}>
                    <input
                      type="checkbox"
                      checked={newPwaHostnamePrimary}
                      onChange={(e) => setNewPwaHostnamePrimary(e.target.checked)}
                    />
                    Primary
                  </label>
                  <button
                    type="button"
                    onClick={addPwaHostname}
                    disabled={pwaHostnameSaving || !newPwaHostname.trim()}
                    style={{ ...generalBtn, opacity: pwaHostnameSaving || !newPwaHostname.trim() ? 0.6 : 1 }}
                  >
                    Add Hostname
                  </button>
                </div>

                <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
                  {pwaHostnames.length === 0 ? (
                    <div style={{ color: "#666", fontStyle: "italic" }}>No PWA hostnames configured.</div>
                  ) : (
                    pwaHostnames.map((host) => (
                      <div
                        key={host.id}
                        style={{
                          display: "grid",
                          gridTemplateColumns: isMobile ? "1fr" : "minmax(220px, 1fr) auto auto auto",
                          gap: 10,
                          alignItems: "center",
                          border: "1px solid #eee",
                          borderRadius: 8,
                          padding: 10,
                        }}
                      >
                        <div>
                          <div style={{ fontWeight: 700, wordBreak: "break-word" }}>{host.hostname}</div>
                          <div style={{ color: "#666", fontSize: 12 }}>
                            {host.is_primary ? "Primary hostname" : "Alias"}
                          </div>
                        </div>
                        <label style={{ display: "flex", gap: 6, alignItems: "center", fontSize: 13 }}>
                          <input
                            type="checkbox"
                            checked={!!host.active}
                            disabled={pwaHostnameSaving}
                            onChange={(e) => updatePwaHostname(host.id, { active: e.target.checked })}
                          />
                          Active
                        </label>
                        <button
                          type="button"
                          disabled={pwaHostnameSaving || host.is_primary}
                          onClick={() => updatePwaHostname(host.id, { is_primary: true, active: true })}
                          style={{ ...generalBtn, opacity: pwaHostnameSaving || host.is_primary ? 0.6 : 1 }}
                        >
                          {host.is_primary ? "Primary" : "Make Primary"}
                        </button>
                        <button
                          type="button"
                          disabled={pwaHostnameSaving}
                          onClick={() => deletePwaHostname(host.id, host.hostname)}
                          style={{ ...dangerBtn, opacity: pwaHostnameSaving ? 0.6 : 1 }}
                        >
                          Remove
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                <button
                  type="button"
                  onClick={savePwaSettings}
                  disabled={pwaSaving || pwaUploading || pwaHostnameSaving || !!pwaSplashBusy}
                  style={{
                    ...actionBtn,
                    opacity: pwaSaving || pwaUploading || pwaHostnameSaving || pwaSplashBusy ? 0.65 : 1,
                    cursor: pwaSaving ? "progress" : "pointer",
                  }}
                >
                  {pwaSaving ? "Saving..." : "Save PWA Settings"}
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Integrations */}
      {activeTab === "integrations" && (
        <div style={{ ...sectionStyle }}>
          <div style={{ maxWidth: 320 }}>
            <label style={labelStyle}>Tenant ID</label>
            <input
              type="number"
              value={tenantId ?? ""}
              readOnly
              disabled
              style={{ ...inputStyle, backgroundColor: "#f7f7f7", cursor: "not-allowed" }}
              title="Managed in Super Admin (licensing)"
            />
            <div style={{ color: "#666", fontStyle: "italic", marginTop: 6 }}>
              {spLoading ? "Loading SharePoint settings..." : sp?.id ? `SharePoint Settings ID: ${sp.id}` : "No SharePoint settings yet"}
            </div>
          </div>

          {/* SharePoint */}
          <div style={{ borderTop: "1px solid #eee", paddingTop: 10 }}>
            <h3 style={{ margin: 0, fontFamily: theme.font_header }}>SharePoint Integration</h3>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 20, marginTop: 10 }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 6 }}>
                  <input
                    id="sp-enabled"
                    type="checkbox"
                    checked={!!sp.enabled}
                    onChange={(e) => setSpField("enabled", e.target.checked)}
                  />
                  <label htmlFor="sp-enabled" style={{ fontWeight: 600 }}>Enabled</label>
                </div>

                <label style={labelStyle}>Domain</label>
                <input
                  type="text"
                  placeholder="contoso.sharepoint.com"
                  value={sp.domain || ""}
                  onChange={(e) => setSpField("domain", e.target.value)}
                  style={inputStyle}
                />

                <label style={labelStyle}>Site Directory</label>
                <input
                  type="text"
                  placeholder="sites"
                  value={sp.site_directory || ""}
                  onChange={(e) => setSpField("site_directory", e.target.value)}
                  style={inputStyle}
                />

                <label style={labelStyle}>Root Path (optional)</label>
                <input
                  type="text"
                  placeholder="/"
                  value={sp.root_path || "/"}
                  onChange={(e) => setSpField("root_path", e.target.value)}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Microsoft Entra Tenant ID (GUID)</label>
                <input
                  type="text"
                  value={sp.aad_tenant_id || ""}
                  onChange={(e) => setSpField("aad_tenant_id", e.target.value)}
                  style={inputStyle}
                />

                <label style={labelStyle}>Client ID</label>
                <input
                  type="text"
                  value={sp.client_id || ""}
                  onChange={(e) => setSpField("client_id", e.target.value)}
                  style={inputStyle}
                />

                <label style={labelStyle}>Client Secret</label>
                <input
                  type="password"
                  value={sp.client_secret_plain || ""}
                  onChange={(e) => setSpField("client_secret_plain", e.target.value)}
                  style={inputStyle}
                  placeholder={sp.access_token_masked ? "•••••• (secret set; enter to rotate)" : "Enter new secret"}
                />
                {sp.access_token_masked && (
                  <div style={{ color: "#666", fontSize: 12, marginTop: 6 }}>
                    Secret set • masked: <span style={{ fontFamily: "monospace" }}>{sp.access_token_masked}</span>
                  </div>
                )}

                <label style={labelStyle}>Site ID</label>
                <input
                  type="text"
                  value={sp.site_id || ""}
                  onChange={(e) => setSpField("site_id", e.target.value)}
                  style={inputStyle}
                />

                <label style={labelStyle}>Clients Drive ID</label>
                <input
                  type="text"
                  value={sp.clients_drive_id || ""}
                  onChange={(e) => setSpField("clients_drive_id", e.target.value)}
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={{ display: "flex", gap: 20, marginTop: 10, color: "#666", fontSize: 13 }}>
              <div>Token Expires: {sp.token_expires_at ? new Date(sp.token_expires_at).toLocaleString() : "—"}</div>
              <div>Last Sync: {sp.last_sync_at ? new Date(sp.last_sync_at).toLocaleString() : "—"}</div>
              <div>Last Error: {sp.last_error || "—"}</div>
            </div>

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: isMobile ? "stretch" : "center", gap: 10, marginTop: 12, flexDirection: isMobile ? "column" : "row" }}>
              {spTest && (
                <div
                  style={{
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: `1px solid ${spTest.ok ? "#2e7d32" : "#c62828"}`,
                    background: spTest.ok ? "#e8f5e9" : "#ffebee",
                    color: spTest.ok ? "#2e7d32" : "#c62828",
                    fontSize: 13,
                    maxWidth: "70%",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                  title={(spTest.error_detail || spTest.message || "").toString()}
                >
                  <strong>{spTest.ok ? "OK" : "Failed"}:</strong> {spTest.message}
                  {typeof spTest.item_count === "number" && <span style={{ marginLeft: 8 }}>• items: {spTest.item_count}</span>}
                  {typeof spTest.token_acquired_ms === "number" && <span style={{ marginLeft: 8 }}>• token: {spTest.token_acquired_ms} ms</span>}
                  {typeof spTest.drive_children_ms === "number" && <span style={{ marginLeft: 8 }}>• list: {spTest.drive_children_ms} ms</span>}
                </div>
              )}

              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  onClick={handleTestSharePoint}
                  disabled={spTesting || spLoading || !tenantId || !sp.enabled}
                  style={{
                    background: "var(--btn-general-normal, #222)",
                    color: "#fff",
                    padding: "6px 10px",
                    border: "none",
                    borderRadius: 8,
                    cursor: spTesting ? "progress" : "pointer",
                    fontWeight: 600,
                    opacity: spTesting ? 0.85 : 1,
                  }}
                  title={!sp.enabled ? "Enable SharePoint to test" : "Calls /api/sharepoint/settings/test"}
                >
                  {spTesting ? "Testing..." : "Test SharePoint"}
                </button>

                <button onClick={saveSharePoint} disabled={spSaving || spLoading || !tenantId} style={actionBtn}>
                  {spSaving ? "Saving..." : sp?.id ? "Save SharePoint" : "Create SharePoint"}
                </button>
              </div>
            </div>
          </div>

          {/* Sage */}
          <div style={{ borderTop: "1px solid #eee", paddingTop: 16 }}>
            <h3 style={{ margin: 0, fontFamily: theme.font_header }}>Sage Integration</h3>
            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 20, marginTop: 10 }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 6 }}>
                  <input
                    id="sage-enabled"
                    type="checkbox"
                    checked={!!sage.enabled}
                    onChange={(e) => setSage((p) => ({ ...p, enabled: e.target.checked }))}
                  />
                  <label htmlFor="sage-enabled" style={{ fontWeight: 600 }}>Enabled</label>
                </div>

                <label style={labelStyle}>Base URL</label>
                <input
                  type="text"
                  value={sage.base_url}
                  onChange={(e) => setSage((p) => ({ ...p, base_url: e.target.value }))}
                  style={inputStyle}
                  placeholder="https://api.sage.com/..."
                />

                <label style={labelStyle}>Company ID</label>
                <input
                  type="text"
                  value={sage.company_id}
                  onChange={(e) => setSage((p) => ({ ...p, company_id: e.target.value }))}
                  style={inputStyle}
                />
              </div>

              <div>
                <label style={labelStyle}>Client ID</label>
                <input
                  type="text"
                  value={sage.client_id}
                  onChange={(e) => setSage((p) => ({ ...p, client_id: e.target.value }))}
                  style={inputStyle}
                />

                <label style={labelStyle}>Client Secret</label>
                <input
                  type="password"
                  value={sage.client_secret_plain}
                  onChange={(e) => setSage((p) => ({ ...p, client_secret_plain: e.target.value }))}
                  style={inputStyle}
                  placeholder={sage.has_secret ? "•••••• (secret is set; enter to rotate)" : "Enter new secret"}
                />
                {sage.has_secret && (
                  <div style={{ color: "#666", fontSize: 12, marginTop: 6 }}>
                    Secret set • preview: <span style={{ fontFamily: "monospace" }}>{sage.secret_preview || "****"}</span>
                  </div>
                )}
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
              <button
                onClick={() => alert("Sage integration API not implemented yet.")}
                style={actionBtn}
              >
                Save Sage (Coming Soon)
              </button>
            </div>
          </div>

          {/* HMRC Reference Data — isolated from Sage and SharePoint */}
          <HmrcReferencePanel mode="integration" />

          {/* SendGrid (Twilio) */}
          <div style={{ borderTop: "1px solid #eee", paddingTop: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
              <h3 style={{ margin: 0, fontFamily: theme.font_header }}>SendGrid (Twilio) Integration</h3>
              {msgLoading && (
                <span style={{ fontSize: 12, color: "#666", fontStyle: "italic" }}>
                  Loading messaging settings…
                </span>
              )}
            </div>

            <div style={{ marginTop: 10 }}>
              <label style={labelStyle}>Channels</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginTop: 6 }}>
                <div className="flex items-center gap-2">
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <i className="fas fa-envelope" aria-hidden="true" />
                    <span>Email</span>
                  </span>
                  <label className="relative inline-flex items-center cursor-pointer ml-2">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={!!messaging.enable_email}
                      onChange={(e) => setMessagingField("enable_email", e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-green-500 transition-colors" />
                    <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full border peer-checked:translate-x-5 transform transition-transform" />
                  </label>
                </div>

                <div className="flex items-center gap-2">
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <i className="fab fa-whatsapp" aria-hidden="true" />
                    <span>WhatsApp</span>
                  </span>
                  <label className="relative inline-flex items-center cursor-pointer ml-2">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={!!messaging.enable_whatsapp}
                      onChange={(e) => setMessagingField("enable_whatsapp", e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-green-500 transition-colors" />
                    <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full border peer-checked:translate-x-5 transform transition-transform" />
                  </label>
                </div>

                <div className="flex items-center gap-2">
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <i className="fas fa-sms" aria-hidden="true" />
                    <span>SMS</span>
                  </span>
                  <label className="relative inline-flex items-center cursor-pointer ml-2">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={!!messaging.enable_sms}
                      onChange={(e) => setMessagingField("enable_sms", e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-green-500 transition-colors" />
                    <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full border peer-checked:translate-x-5 transform transition-transform" />
                  </label>
                </div>

                <div className="flex items-center gap-2">
                  <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                    <i className="fas fa-comments" aria-hidden="true" />
                    <span>Live Chat</span>
                  </span>
                  <label className="relative inline-flex items-center cursor-pointer ml-2">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={!!messaging.enable_livechat}
                      onChange={(e) => setMessagingField("enable_livechat", e.target.checked)}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:bg-green-500 transition-colors" />
                    <div className="absolute left-1 top-1 w-4 h-4 bg-white rounded-full border peer-checked:translate-x-5 transform transition-transform" />
                  </label>
                </div>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 20, marginTop: 16 }}>
              <div>
                <label style={labelStyle}>From Name</label>
                <input
                  type="text"
                  value={messaging.sendgrid_from_name || ""}
                  onChange={(e) => setMessagingField("sendgrid_from_name", e.target.value)}
                  style={inputStyle}
                  placeholder="Sender Name"
                />

                <label style={labelStyle}>From Email</label>
                <input
                  type="email"
                  value={messaging.sendgrid_from_email || ""}
                  onChange={(e) => setMessagingField("sendgrid_from_email", e.target.value)}
                  style={inputStyle}
                  placeholder="no-reply@example.com"
                />

                <label style={labelStyle}>Reply-To Email</label>
                <input
                  type="email"
                  value={messaging.reply_to_email || ""}
                  onChange={(e) => setMessagingField("reply_to_email", e.target.value)}
                  style={inputStyle}
                  placeholder="support@example.com"
                />

                <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 12 }}>
                  <input
                    id="email-dry-run"
                    type="checkbox"
                    checked={!!messaging.email_dry_run}
                    onChange={(e) => setMessagingField("email_dry_run", e.target.checked)}
                  />
                  <label htmlFor="email-dry-run" style={{ fontWeight: 600 }}>
                    Email Dry-Run Mode
                  </label>
                </div>
                <div style={{ color: "#666", fontSize: 12, marginTop: 4 }}>
                  When enabled, emails are logged only and not actually sent.
                </div>

                <div style={{ marginTop: 16 }}>
                  <label style={labelStyle}>Usage Reporting</label>
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6 }}>
                    <input
                      id="send-usage-report"
                      type="checkbox"
                      checked={!!messaging.send_usage_report}
                      onChange={(e) => setMessagingField("send_usage_report", e.target.checked)}
                    />
                    <label htmlFor="send-usage-report" style={{ fontWeight: 600 }}>
                      Send monthly usage report
                    </label>
                  </div>
                  <div style={{ marginTop: 8, maxWidth: 360 }}>
                    <label style={labelStyle}>Report Recipient Email</label>
                    <input
                      type="email"
                      value={messaging.usage_report_email || ""}
                      onChange={(e) => setMessagingField("usage_report_email", e.target.value)}
                      style={inputStyle}
                      placeholder="alerts@example.com"
                    />
                  </div>
                </div>

                <div style={{ marginTop: 16 }}>
                  <label style={labelStyle}>Test Targets</label>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
                      gap: 16,
                      marginTop: 6,
                      maxWidth: 600,
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>
                        Test Email Recipient
                      </div>
                      <input
                        type="email"
                        value={messaging.test_email || ""}
                        onChange={(e) => setMessagingField("test_email", e.target.value)}
                        style={inputStyle}
                        placeholder="you@example.com"
                      />
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: "#666", marginBottom: 4 }}>
                        Test WhatsApp / SMS Number
                      </div>
                      <input
                        type="text"
                        value={messaging.test_number || ""}
                        onChange={(e) => setMessagingField("test_number", e.target.value)}
                        style={inputStyle}
                        placeholder="+441234567890"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <label style={labelStyle}>WhatsApp From Number</label>
                <input
                  type="text"
                  value={messaging.whatsapp_from_number || ""}
                  onChange={(e) => setMessagingField("whatsapp_from_number", e.target.value)}
                  style={inputStyle}
                  placeholder="+441234567890"
                />

                <label style={labelStyle}>SMS From Number</label>
                <input
                  type="text"
                  value={messaging.sms_from_number || ""}
                  onChange={(e) => setMessagingField("sms_from_number", e.target.value)}
                  style={inputStyle}
                  placeholder="+447308502005"
                />

                <label style={labelStyle}>SMS Sender ID (Alphanumeric)</label>
                <input
                  type="text"
                  value={messaging.sms_sender_id || ""}
                  onChange={(e) => setMessagingField("sms_sender_id", e.target.value)}
                  style={inputStyle}
                  placeholder="PAULCASTLE"
                />

                <label style={labelStyle}>WhatsApp Business Name</label>
                <input
                  type="text"
                  value={messaging.whatsapp_business_name || ""}
                  onChange={(e) => setMessagingField("whatsapp_business_name", e.target.value)}
                  style={inputStyle}
                  placeholder="Paul Castle Associates"
                />

                <label style={labelStyle}>Twilio Account SID</label>
                <input
                  type="text"
                  value={messaging.twilio_account_sid || ""}
                  onChange={(e) => setMessagingField("twilio_account_sid", e.target.value)}
                  style={inputStyle}
                  placeholder={
                    messaging.twilio_account_sid_masked
                      ? "AC•••• (SID set; enter to rotate)"
                      : "ACXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
                  }
                />
                {messaging.twilio_account_sid_masked && (
                  <div style={{ color: "#666", fontSize: 12, marginTop: 4 }}>
                    SID set • masked:{" "}
                    <span style={{ fontFamily: "monospace" }}>
                      {messaging.twilio_account_sid_masked}
                    </span>
                  </div>
                )}

                <label style={labelStyle}>Twilio Messaging Service SID</label>
                <input
                  type="text"
                  value={messaging.twilio_messaging_service_sid || ""}
                  onChange={(e) =>
                    setMessagingField("twilio_messaging_service_sid", e.target.value)
                  }
                  style={inputStyle}
                  placeholder={
                    messaging.twilio_messaging_service_sid_masked
                      ? "MG•••• (SID set; enter to rotate)"
                      : "MGXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
                  }
                />
                {messaging.twilio_messaging_service_sid_masked && (
                  <div style={{ color: "#666", fontSize: 12, marginTop: 4 }}>
                    Messaging Service SID set • masked:{" "}
                    <span style={{ fontFamily: "monospace" }}>
                      {messaging.twilio_messaging_service_sid_masked}
                    </span>
                  </div>
                )}

                <label style={labelStyle}>WhatsApp Message Template SID</label>
                <input
                  type="text"
                  value={messaging.whatsapp_template_sid || ""}
                  onChange={(e) => setMessagingField("whatsapp_template_sid", e.target.value)}
                  style={inputStyle}
                  placeholder="HXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXXX"
                />
                <div style={{ color: "#666", fontSize: 12, marginTop: 4 }}>
                  Used as Twilio <code>ContentSid</code> when sending WhatsApp template
                  messages. Leave blank to send freeform <code>Body</code> text instead.
                </div>

                <label style={labelStyle}>SendGrid API Key</label>
                <input
                  type="password"
                  value={messaging.sendgrid_api_key_plain}
                  onChange={(e) => setMessagingField("sendgrid_api_key_plain", e.target.value)}
                  style={inputStyle}
                  placeholder={
                    messaging.sendgrid_api_key_masked
                      ? "•••••• (key set; enter to rotate)"
                      : "Enter SendGrid API key"
                  }
                />
                {messaging.sendgrid_api_key_masked && (
                  <div style={{ color: "#666", fontSize: 12, marginTop: 4 }}>
                    Key set • masked:{" "}
                    <span style={{ fontFamily: "monospace" }}>
                      {messaging.sendgrid_api_key_masked}
                    </span>
                  </div>
                )}

                <label style={labelStyle}>Twilio API Key (Auth Token)</label>
                <input
                  type="password"
                  value={messaging.twilio_api_key_plain}
                  onChange={(e) => setMessagingField("twilio_api_key_plain", e.target.value)}
                  style={inputStyle}
                  placeholder={
                    messaging.twilio_api_key_masked
                      ? "•••••• (key set; enter to rotate)"
                      : "Enter Twilio auth token"
                  }
                />
                {messaging.twilio_api_key_masked && (
                  <div style={{ color: "#666", fontSize: 12, marginTop: 4 }}>
                    Key set • masked:{" "}
                    <span style={{ fontFamily: "monospace" }}>
                      {messaging.twilio_api_key_masked}
                    </span>
                  </div>
                )}
              </div>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 12,
                marginTop: 16,
                flexWrap: "wrap",
              }}
            >
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <button type="button" onClick={() => handleTestMessaging("email")} style={generalBtn}>
                  Test Email
                </button>
                <button type="button" onClick={() => handleTestMessaging("whatsapp")} style={generalBtn}>
                  Test WhatsApp
                </button>
                <button type="button" onClick={() => handleTestMessaging("sms")} style={generalBtn}>
                  Test SMS
                </button>
              </div>

              <button type="button" onClick={handleSaveMessaging} style={actionBtn}>
                Save Messaging Settings
              </button>
            </div>
          </div>

          {/* Weather */}
          <div style={{ borderTop: "1px solid #eee", paddingTop: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
              <h3 style={{ margin: 0, fontFamily: theme.font_header }}>Met Office Weather Integration</h3>
              {weatherLoading && (
                <span style={{ fontSize: 12, color: "#666", fontStyle: "italic" }}>
                  Loading weather settings…
                </span>
              )}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 20, marginTop: 10 }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 6 }}>
                  <input
                    id="weather-enabled"
                    type="checkbox"
                    checked={!!weather.enable_weather}
                    onChange={(e) => setWeatherField("enable_weather", e.target.checked)}
                  />
                  <label htmlFor="weather-enabled" style={{ fontWeight: 600 }}>Enabled</label>
                </div>

                <label style={labelStyle}>Default Source</label>
                <input
                  type="text"
                  value={weather.default_source || ""}
                  onChange={(e) => setWeatherField("default_source", e.target.value)}
                  style={inputStyle}
                  placeholder="BD1"
                  disabled={weatherLoading}
                />
                <div style={{ color: "#666", fontSize: 12, marginTop: 4 }}>
                  Used when no source override is provided (e.g. BD1).
                </div>

                <label style={labelStyle}>Cache TTL (seconds)</label>
                <input
                  type="number"
                  min={0}
                  max={86400}
                  value={weather.cache_ttl_seconds ?? 300}
                  onChange={(e) => setWeatherField("cache_ttl_seconds", e.target.value)}
                  style={inputStyle}
                  disabled={weatherLoading}
                />
                <div style={{ color: "#666", fontSize: 12, marginTop: 4 }}>
                  0–86400. Backend also caches (Redis preferred; in-memory fallback).
                </div>
              </div>

              <div>
                <label style={labelStyle}>Global Spot API Key</label>
                <input
                  type="password"
                  value={weather.metoffice_global_spot_api_key_plain || ""}
                  onChange={(e) => setWeatherField("metoffice_global_spot_api_key_plain", e.target.value)}
                  style={inputStyle}
                  placeholder={
                    weather.metoffice_global_spot_api_key_masked
                      ? "•••••• (key set; enter to rotate)"
                      : "Enter Global Spot key"
                  }
                  disabled={weatherLoading}
                />
                {weather.metoffice_global_spot_api_key_masked && (
                  <div style={{ color: "#666", fontSize: 12, marginTop: 4 }}>
                    Key set • masked:{" "}
                    <span style={{ fontFamily: "monospace" }}>
                      {weather.metoffice_global_spot_api_key_masked}
                    </span>
                  </div>
                )}

                <label style={labelStyle}>BPF API Key</label>
                <input
                  type="password"
                  value={weather.metoffice_bpf_api_key_plain || ""}
                  onChange={(e) => setWeatherField("metoffice_bpf_api_key_plain", e.target.value)}
                  style={inputStyle}
                  placeholder={
                    weather.metoffice_bpf_api_key_masked
                      ? "•••••• (key set; enter to rotate)"
                      : "Enter BPF key"
                  }
                  disabled={weatherLoading}
                />
                {weather.metoffice_bpf_api_key_masked && (
                  <div style={{ color: "#666", fontSize: 12, marginTop: 4 }}>
                    Key set • masked:{" "}
                    <span style={{ fontFamily: "monospace" }}>
                      {weather.metoffice_bpf_api_key_masked}
                    </span>
                  </div>
                )}
                <div style={{ color: "#666", fontSize: 12, marginTop: 6 }}>
                  Stored now; BPF route/service usage is future-facing.
                </div>
              </div>
            </div>

            {weatherTest && (
              <div
                style={{
                  marginTop: 12,
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: `1px solid ${weatherTest.ok === false ? "#c62828" : "#2e7d32"}`,
                  background: weatherTest.ok === false ? "#ffebee" : "#e8f5e9",
                  color: weatherTest.ok === false ? "#c62828" : "#2e7d32",
                  fontSize: 13,
                }}
                title={(weatherTest.error_detail || weatherTest.message || "").toString()}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div>
                    <strong>{weatherTest.ok === false ? "Failed" : "OK"}:</strong>{" "}
                    {weatherTest.message || "Forecast retrieved"}
                  </div>
                  <div style={{ color: "inherit", opacity: 0.95 }}>
                    {typeof weatherTest.cached === "boolean" && (
                      <span style={{ marginRight: 10 }}>cached: {weatherTest.cached ? "yes" : "no"}</span>
                    )}
                    {typeof weatherTest.cache_ttl_seconds === "number" && (
                      <span>ttl: {weatherTest.cache_ttl_seconds}s</span>
                    )}
                  </div>
                </div>

                <div style={{ marginTop: 8, display: "flex", gap: 16, flexWrap: "wrap", color: "inherit" }}>
                  {weatherTest.model_run && <div>model_run: <span style={{ fontFamily: "monospace" }}>{String(weatherTest.model_run)}</span></div>}
                  {typeof weatherTest.first_temp_c === "number" && <div>first_temp: <span style={{ fontFamily: "monospace" }}>{weatherTest.first_temp_c}°C</span></div>}
                  {typeof weatherTest.first_weather_code !== "undefined" && <div>weather_code: <span style={{ fontFamily: "monospace" }}>{String(weatherTest.first_weather_code)}</span></div>}
                </div>
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
              <div style={{ color: "#666", fontSize: 12, fontStyle: "italic" }}>
                Weather calls are hard-stopped when disabled.
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={handleTestWeather}
                  disabled={weatherTesting || weatherLoading || !tenantId || !weather.enable_weather}
                  style={{
                    ...generalBtn,
                    cursor: weatherTesting ? "progress" : "pointer",
                    opacity: weatherTesting ? 0.85 : 1,
                  }}
                  title={!weather.enable_weather ? "Enable weather to test" : "Calls /api/weather/test"}
                >
                  {weatherTesting ? "Testing..." : "Test Forecast"}
                </button>

                <button
                  type="button"
                  onClick={saveWeather}
                  disabled={weatherSaving || weatherLoading || !tenantId}
                  style={actionBtn}
                >
                  {weatherSaving ? "Saving..." : "Save Weather Settings"}
                </button>
              </div>
            </div>
          </div>

          {/* Traffic */}
          <div style={{ borderTop: "1px solid #eee", paddingTop: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 }}>
              <h3 style={{ margin: 0, fontFamily: theme.font_header }}>TomTom Live Traffic Integration</h3>
              {trafficLoading && (
                <span style={{ fontSize: 12, color: "#666", fontStyle: "italic" }}>
                  Loading traffic settings…
                </span>
              )}
            </div>

            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 20, marginTop: 10 }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 6 }}>
                  <input
                    id="traffic-enabled"
                    type="checkbox"
                    checked={!!traffic.is_enabled}
                    onChange={(e) => setTrafficField("is_enabled", e.target.checked)}
                  />
                  <label htmlFor="traffic-enabled" style={{ fontWeight: 600 }}>Enabled</label>
                </div>

                <label style={labelStyle}>Provider</label>
                <input
                  type="text"
                  value={traffic.provider || "tomtom"}
                  readOnly
                  disabled
                  style={{ ...inputStyle, backgroundColor: "#f7f7f7", cursor: "not-allowed" }}
                />

                <label style={labelStyle}>API Key</label>
                <input
                  type="text"
                  value={traffic.api_key || ""}
                  onChange={(e) => setTrafficField("api_key", e.target.value)}
                  style={inputStyle}
                  placeholder="Enter TomTom API key"
                  disabled={trafficLoading}
                />
                <div style={{ color: "#666", fontSize: 12, marginTop: 4 }}>
                  Stored tenant-side in <code>live_traffic_settings</code>.
                </div>

                <label style={labelStyle}>Base URL</label>
                <input
                  type="text"
                  value={traffic.base_url || ""}
                  onChange={(e) => setTrafficField("base_url", e.target.value)}
                  style={inputStyle}
                  placeholder="https://api.tomtom.com/traffic/services/4/flowSegmentData/absolute/10/json"
                  disabled={trafficLoading}
                />

                <label style={labelStyle}>Notes</label>
                <textarea
                  value={traffic.notes || ""}
                  onChange={(e) => setTrafficField("notes", e.target.value)}
                  style={{ ...inputStyle, height: 84, resize: "vertical" }}
                  placeholder="Optional operational notes"
                  disabled={trafficLoading}
                />
              </div>

              <div>
                <label style={labelStyle}>Unit</label>
                <select
                  value={traffic.unit || "KMPH"}
                  onChange={(e) => setTrafficField("unit", e.target.value)}
                  style={selectStyle}
                  disabled={trafficLoading}
                >
                  <option value="KMPH">KMPH</option>
                  <option value="MPH">MPH</option>
                </select>

                <label style={labelStyle}>Zoom</label>
                <input
                  type="number"
                  min={1}
                  max={22}
                  value={traffic.zoom ?? 10}
                  onChange={(e) => setTrafficField("zoom", e.target.value)}
                  style={inputStyle}
                  disabled={trafficLoading}
                />

                <label style={labelStyle}>Cache TTL (seconds)</label>
                <input
                  type="number"
                  min={0}
                  value={traffic.cache_ttl_seconds ?? 300}
                  onChange={(e) => setTrafficField("cache_ttl_seconds", e.target.value)}
                  style={inputStyle}
                  disabled={trafficLoading}
                />

                <label style={labelStyle}>Connect Timeout (seconds)</label>
                <input
                  type="number"
                  min={1}
                  max={60}
                  value={traffic.connect_timeout_seconds ?? 3}
                  onChange={(e) => setTrafficField("connect_timeout_seconds", e.target.value)}
                  style={inputStyle}
                  disabled={trafficLoading}
                />

                <label style={labelStyle}>Read Timeout (seconds)</label>
                <input
                  type="number"
                  min={1}
                  max={120}
                  value={traffic.read_timeout_seconds ?? 6}
                  onChange={(e) => setTrafficField("read_timeout_seconds", e.target.value)}
                  style={inputStyle}
                  disabled={trafficLoading}
                />

                <label style={labelStyle}>Max Requests Per Job Check</label>
                <input
                  type="number"
                  min={1}
                  max={100}
                  value={traffic.max_requests_per_job_check ?? 4}
                  onChange={(e) => setTrafficField("max_requests_per_job_check", e.target.value)}
                  style={inputStyle}
                  disabled={trafficLoading}
                />

                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 14 }}>
                  <input
                    id="traffic-persist"
                    type="checkbox"
                    checked={!!traffic.persist_snapshots}
                    onChange={(e) => setTrafficField("persist_snapshots", e.target.checked)}
                  />
                  <label htmlFor="traffic-persist" style={{ fontWeight: 600 }}>
                    Persist Historical Snapshots
                  </label>
                </div>

                <div style={{ color: "#666", fontSize: 12, marginTop: 4 }}>
                  Saves normalized traffic decisions to <code>traffic_intelligence_snapshots</code>.
                </div>
              </div>
            </div>

            {trafficTest && (
              <div
                style={{
                  marginTop: 12,
                  padding: "10px 12px",
                  borderRadius: 8,
                  border: `1px solid ${trafficTest.ok === false ? "#c62828" : "#2e7d32"}`,
                  background: trafficTest.ok === false ? "#ffebee" : "#e8f5e9",
                  color: trafficTest.ok === false ? "#c62828" : "#2e7d32",
                  fontSize: 13,
                }}
                title={(trafficTest.error_detail || trafficTest.message || "").toString()}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <div>
                    <strong>{trafficTest.ok === false ? "Failed" : "OK"}:</strong>{" "}
                    {trafficTest.message || trafficTest.summary || "Traffic intelligence retrieved"}
                  </div>
                </div>

                {trafficTest.ok !== false && (
                  <div style={{ marginTop: 8, display: "flex", gap: 16, flexWrap: "wrap" }}>
                    {trafficTest.source && (
                      <div>source: <span style={{ fontFamily: "monospace" }}>{String(trafficTest.source)}</span></div>
                    )}
                    {trafficTest.traffic?.road_name && (
                      <div>road: <span style={{ fontFamily: "monospace" }}>{String(trafficTest.traffic.road_name)}</span></div>
                    )}
                    {typeof trafficTest.traffic?.current_speed === "number" && (
                      <div>current: <span style={{ fontFamily: "monospace" }}>{trafficTest.traffic.current_speed}</span></div>
                    )}
                    {typeof trafficTest.traffic?.free_flow_speed === "number" && (
                      <div>free flow: <span style={{ fontFamily: "monospace" }}>{trafficTest.traffic.free_flow_speed}</span></div>
                    )}
                    {trafficTest.assessment?.status && (
                      <div>status: <span style={{ fontFamily: "monospace" }}>{String(trafficTest.assessment.status)}</span></div>
                    )}
                    {trafficTest.assessment?.recommendation && (
                      <div>recommendation: <span style={{ fontFamily: "monospace" }}>{String(trafficTest.assessment.recommendation)}</span></div>
                    )}
                  </div>
                )}
              </div>
            )}

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
              <div style={{ color: "#666", fontSize: 12, fontStyle: "italic" }}>
                Test uses a fixed Northampton coordinate against the live intelligence endpoint.
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={handleTestTraffic}
                  disabled={trafficTesting || trafficLoading || !tenantId || !traffic.is_enabled}
                  style={{
                    ...generalBtn,
                    cursor: trafficTesting ? "progress" : "pointer",
                    opacity: trafficTesting ? 0.85 : 1,
                  }}
                  title={!traffic.is_enabled ? "Enable live traffic to test" : "Calls /api/traffic/intelligence"}
                >
                  {trafficTesting ? "Testing..." : "Test Traffic"}
                </button>

                <button
                  type="button"
                  onClick={saveTraffic}
                  disabled={trafficSaving || trafficLoading || !tenantId}
                  style={actionBtn}
                >
                  {trafficSaving ? "Saving..." : "Save Traffic Settings"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Resources */}
      {activeTab === "resources" && (
        <div style={{ ...sectionStyle, minWidth: 0, overflowX: "auto" }}>
          <ResourcesTabContainer key={resourcesKey} />
        </div>
      )}

      {/* Records */}
      {activeTab === "policies" && (
        <div style={{ ...sectionStyle }}>
          <HmrcReferencePanel mode="policies" />
        </div>
      )}

      {activeTab === "records" && (
        <div style={{ ...sectionStyle, minWidth: 0, overflowX: "auto" }}>
          <RecordsManagementPanel />
        </div>
      )}

      {/* Licence */}
      {activeTab === "licence" && (
        <div style={{ ...sectionStyle }}>
          <div style={{ color: "#666" }}>
            Licence Manager coming soon. This area will display licence status, seats, and renewal info.
          </div>
        </div>
      )}
    </div>
  );
};

// Accordion header
const AccordionHeader = ({ title, open, onToggle }) => {
  const { theme } = useTheme();
  const color = open ? theme.accent_color : theme.accent_hover;
  return (
    <div
      onClick={onToggle}
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "12px 14px",
        background: "#f1f1f1",
        border: "1px solid #ddd",
        borderRadius: "8px",
        cursor: "pointer",
        userSelect: "none",
        color,
        fontWeight: 700,
      }}
      title="Click to expand/collapse"
    >
      <span>{title}</span>
      <span style={{ fontSize: 18 }}>{open ? "−" : "+"}</span>
    </div>
  );
};

// Small color input helper
const ColorInput = ({ value, onChange }) => {
  const v = safeColor(value);
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginTop: 6 }}>
      <input
        type="color"
        value={v}
        onChange={(e) => onChange(safeColor(e.target.value))}
        style={{
          width: 34,
          height: 34,
          padding: 0,
          border: "1px solid #ccc",
          borderRadius: 6,
          background: "#fff",
          boxSizing: "border-box",
          display: "inline-block",
        }}
      />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{
          padding: "8px",
          borderRadius: "6px",
          border: "1px solid #ccc",
          marginTop: "6px",
          width: "min(120px, calc(100vw - 110px))",
          maxWidth: 120,
          height: 34,
          boxSizing: "border-box",
        }}
      />
    </div>
  );
};

export default SystemPage;